/**
 * Publicações em Grupos (spec 18) — a tela do módulo.
 *
 * Cinco abas numa rota só (`/publicacoes?aba=…`): **Início** (o operacional: próximas, hoje, em
 * execução, o que precisa de atenção, últimas — e os poucos insights que tiverem amostra), **Grupos**
 * (sincronizar, buscar, filtrar, etiquetar em lote), **Canais** e **Publicações** (a lista e o
 * calendário, dois modos de ver a mesma aba — spec 23, jornada 5). **Nova publicação** (conteúdo →
 * grupos → quando e por qual número) ocupa a tela no lugar das abas. O DETALHE de uma publicação é **página
 * própria**, `/publicacoes/<id>` (`PublicacaoDetalhe`): execuções, entregas por grupo, resultado,
 * analytics — decisão do owner em 05/09, depois de mandar a primeira de verdade (*"prefiro que tenha
 * uma tela exclusiva pra isso"*).
 *
 * ## As famílias (spec 22)
 *
 * - A tela do módulo: `PaginaInteira` + `CabecalhoDaTela` + as abas (`Segmentado`); cada aba divide o
 *   resto da altura e rola por dentro. Início é um Cockpit (faixas + `GradeDeCartoes`), Publicações é
 *   a `ListView`, Grupos e Calendário são cartões elásticos.
 * - Nova publicação: `PaginaDeCadastro` em três passos (Conteúdo → Grupos → Quando e por qual número), com o
 *   resumo fixo ao lado e a primária "Agendar em N grupos" — a prévia se recalcula sozinha (spec 23, jornada 5).
 * - O detalhe: `PaginaDeRegistro` + `Destaque` + a faixa de números; o resto rola no principal, e quem fez
 *   o quê é a `Atividade` ao lado.
 *
 * ## O que a tela NÃO faz
 *
 * Não conta grupos, não calcula audiência, não escolhe número: tudo isso vem do servidor, pela
 * prévia. E **N/A não é zero**: uma métrica `disponivel: false` aparece como "N/A" com o motivo,
 * nunca como 0 (spec 18 §42).
 */

import { useEffect, useId, useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  EstadoDaFalha, Etiqueta, Metricas, chamar, contar, falhaDe, identificadorLegivel, textos, useSessao,
  type FalhaDaApi, type Metrica as MetricaDoHugi,
} from "@hugi/ui";
import {
  Atividade, BarraDeAcoes, BlocoDoCadastro, CabecalhoDaTela, CampoDoCadastro, Carregando, Destaque, Dica, Erro, GradeDeCartoes, ListView,
  PaginaDeCadastro, PaginaDeRegistro, PaginaInteira, PainelDeIndicadores, Selo, SemPermissao, Vazio,
  type AcaoDoRegistro, type CartaoDaGrade, type ColunaDaLista, type Indicador, type VistaDaLista,
} from "@hugi/ui/kit";
import { AlertTriangle, CalendarClock, CalendarDays, FileText, Heart, Image as ImageIcon, List, ListChecks, MousePointerClick, Send, Tag, Type, UsersRound, Video } from "lucide-react";
import {
  administrarGrupo, agirNaPublicacao, operarCanal, criarEtiquetaDeGrupo, criarPublicacao, etiquetarGrupos, gerenciarGrupo, guardarMidiaDePublicacao, previaDePublicacao, sincronizarGrupos,
  trocarRemetenteDaPublicacao, type RemetenteDaPublicacao, useAnaliseDaPublicacao, useCalendarioDePublicacoes, useEntregasDaPublicacao, useGrupos, usePublicacao, usePublicacoes,
  useResumoDePublicacoes,
  type CanalDoWhatsApp, type ConexaoParaPublicar, type EstadoDePublicacao, type Grupo, type ItemDoCalendario, type Metrica, type PreviaDaPublicacao, type Publicacao,
} from "./dados.js";
import "./hugi-grupos.css";
import { ModoDaAba, ModuloDeGrupos, abaDaSecao } from "./modulo-de-grupos.js";

type Aba = "inicio" | "grupos" | "canais" | "publicacoes" | "calendario" | "nova";

type TomHugi = "ok" | "degradado" | "ruim" | "neutro";
type TomDoKit = "neutro" | "sucesso" | "atencao" | "perigo";
const TOM: Readonly<Record<TomHugi, TomDoKit>> = { ok: "sucesso", degradado: "atencao", ruim: "perigo", neutro: "neutro" };

const ESTADO: Readonly<Record<EstadoDePublicacao | "prevista", { rotulo: string; tom: TomHugi }>> = {
  agendada: { rotulo: "Agendada", tom: "neutro" },
  executando: { rotulo: "Enviando", tom: "ok" },
  pausada: { rotulo: "Pausada", tom: "degradado" },
  aguardando_conexao: { rotulo: "Esperando a conexão", tom: "degradado" },
  freada: { rotulo: "Pausada pelo freio", tom: "ruim" },
  concluida: { rotulo: "Concluída", tom: "ok" },
  parcialmente_concluida: { rotulo: "Parcialmente concluída", tom: "degradado" },
  falhou: { rotulo: "Falhou", tom: "ruim" },
  cancelada: { rotulo: "Cancelada", tom: "ruim" },
  prevista: { rotulo: "Prevista", tom: "neutro" },
};

/** O selo de um estado de publicação (ou de execução, que usa o mesmo vocabulário). */
function SeloDoEstado(props: { readonly estado: string }) {
  const e = ESTADO[props.estado as EstadoDePublicacao];
  return <Selo tom={TOM[e?.tom ?? "neutro"]}>{e?.rotulo ?? props.estado}</Selo>;
}

const TIPO_DE_GRUPO: Readonly<Record<string, string>> = { everyone: "Conversacional", admins_only: "Somente administradores", unknown: "Desconhecido" };
const FORMATO: Readonly<Record<string, string>> = {
  texto: "Texto", imagem: "Imagem", video: "Vídeo", documento: "Documento", enquete: "Enquete", botoes: "Botões",
};
/** Os que carregam arquivo. Enquete e botões não têm o que subir. */
const COM_ARQUIVO = ["imagem", "video", "documento"] as const;
const temArquivo = (t: string) => (COM_ARQUIVO as readonly string[]).includes(t);
const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const TITULO = "Grupos";
const FRASE = "Sincronize os grupos do número, etiquete, e agende o que sai em cada um — por qual conexão você escolher. O resultado é por grupo.";

/** "04/09 09:36" — a tabela por grupo não precisa do ano. */
const horaCurta = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).replace(",", "") : "—");
const quando = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");
/** Criada e Fim da lista, com hora — publicação acontece várias vezes no mesmo dia. */
const dataComHora = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
/**
 * O nome de uma conexão nesta tela: apelido, senão o número — **com a máscara de `@hugi/ui`**.
 *
 * Não é o `nomeDaConexao` do `@hugi/ui` porque aqui o último recurso é o `id` da conexão, e não a
 * frase "(sem número)": o seletor de número precisa de algo que distinga duas conexões sem apelido.
 */
const nomeDaConexao = (c: { apelido: string | null; numeroExibicao: string | null; id: string }) =>
  c.apelido ?? (c.numeroExibicao ? identificadorLegivel(c.numeroExibicao) : c.id);

/** Inteiro com sinal: 2 → "+2", -1 → "−1", 0 → "0". */
const sinal = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : "0");

/** Variação com sinal: 0.35 → "+35%", -0.2 → "−20%". */
const sinalPorcento = (x: number) => `${x < 0 ? "−" : "+"}${porcento(Math.abs(x))}`;

/** Fração em porcentagem legível: 0.0456 → "4,6%". */
const porcento = (x: number) => `${(x * 100).toLocaleString("pt-BR", { maximumFractionDigits: x * 100 >= 10 ? 0 : 1 })}%`;

/** Segundos em frase curta: 0 → "menos de um segundo", 118 → "1 min 58 s". */
function duracao(s: number): string {
  if (s < 1) return "menos de um segundo";
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60); const r = s % 60;
  return r ? `${m} min ${r} s` : `${m} min`;
}

function mensagemDa(f: FalhaDaApi | undefined): string | undefined {
  if (!f) return undefined;
  return "erro" in f && f.erro && typeof f.erro === "object" && "mensagem" in f.erro ? String((f.erro as { mensagem?: unknown }).mensagem ?? "") || undefined : undefined;
}

/** A métrica como a spec 18 §42 manda: 0 é medido; N/A é "não observável", com o motivo. */
function Valor(props: { readonly m: Metrica; readonly sufixo?: string; readonly formatar?: (x: number) => string }) {
  if (!props.m.disponivel) return <span title={props.m.motivo ?? undefined} className="is-ausente">N/A</span>;
  if (props.m.valor === null) return <span title={props.m.motivo ?? undefined} className="is-ausente">—</span>;
  return <span title={props.m.motivo ?? undefined}>{props.formatar ? props.formatar(props.m.valor) : props.m.valor.toLocaleString("pt-BR")}{props.sufixo ?? ""}</span>;
}

export function Publicacoes() {
  const sessao = useSessao();
  const podeGerenciar = sessao.permissoes.includes("manage_post");
  const [, navegar] = useLocation();
  const [aba, setAbaLocal] = useState<Aba>(() => {
    if (typeof window === "undefined") return "inicio";
    const a = new URLSearchParams(window.location.search).get("aba") as Aba | null;
    return a && ["inicio", "grupos", "canais", "publicacoes", "calendario", "nova"].includes(a) ? a : "inicio";
  });
  // `?publicacao=<id>` era como o detalhe abria antes de ter página própria. Link velho continua
  // chegando ao lugar certo.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const id = new URLSearchParams(window.location.search).get("publicacao");
    if (id) navegar(`/publicacoes/${encodeURIComponent(id)}`, { replace: true });
  }, [navegar]);
  const abrir = (id: string) => navegar(`/publicacoes/${encodeURIComponent(id)}`);
  const irPara = (nova: Aba) => {
    setAbaLocal(nova);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("aba", nova);
    url.searchParams.delete("publicacao");
    window.history.replaceState(null, "", url);
  };

  if (aba === "nova") return <NovaPublicacao aoFechar={(id) => { if (id) abrir(id); else irPara("inicio"); }} />;

  // A primária é da ABA (o esqueleto do módulo é o mesmo nas quatro rotas — `ModuloDeGrupos`): Visão geral e Publicações
  // agendam; Seus grupos não tem uma (sincronizar é da barra dela, por número).
  const comPrimaria = aba === "inicio" || aba === "publicacoes" || aba === "calendario";
  return (
    <ModuloDeGrupos
      ativa={abaDaSecao(aba)}
      aoTrocarNaPagina={irPara}
      {...(comPrimaria
        ? {
            primaria: (
              <Acao
                tom="primaria"
                desabilitado={!podeGerenciar}
                {...(podeGerenciar ? {} : { motivo: "Criar publicação exige a permissão manage_post." })}
                onClick={() => irPara("nova")}
              >
                Nova publicação
              </Acao>
            ),
          }
        : {})}
    >
      {aba === "inicio" ? <Inicio aoAbrir={abrir} aoIr={irPara} /> : null}
      {aba === "grupos" ? <TelaDeGrupos modo={<ModoDosGrupos ativo="grupos" aoTrocar={irPara} />} /> : null}
      {aba === "canais" ? <TelaDeCanais modo={<ModoDosGrupos ativo="canais" aoTrocar={irPara} />} /> : null}
      {aba === "publicacoes" ? <TelaDePublicacoes aoAbrir={abrir} modo={<ModoDePublicacoes ativo="lista" aoTrocar={irPara} />} /> : null}
      {aba === "calendario" ? <Calendario aoAbrir={abrir} modo={<ModoDePublicacoes ativo="calendario" aoTrocar={irPara} />} /> : null}
    </ModuloDeGrupos>
  );
}

// --- Visão geral -------------------------------------------------------------------------------------

/*
  A VISÃO GERAL (owner, 01/11: "confusa, desorganizada"): eram 10 indicadores em duas faixas e 5 cartões, metade deles
  vazios. Ficou o que pede ação: seis números com ícone (`Metricas`) — o que precisa de atenção e o que está saindo
  primeiro — e até três listas curtas (Precisam de atenção, só quando há; Próximas, com as de hoje no topo; Últimas).
  Os cartões têm a altura do conteúdo (`fit-content`), não a da tela: lista vazia é uma linha, não um cartão gigante.
  Os insights (horário, formato, grupos responsivos) foram para o (i) do engajamento.
*/
function Inicio(props: { readonly aoAbrir: (id: string) => void; readonly aoIr: (aba: Aba) => void }) {
  const q = useResumoDePublicacoes();
  const [cartao, setCartao] = useState<string | undefined>(undefined);
  if (q.isPending) return <div className="v2-cartao"><Carregando>Carregando o resumo…</Carregando></div>;
  if (q.isError) return <div className="v2-cartao"><Falha erro={q.error} aoTentarDeNovo={() => void q.refetch()} /></div>;
  const r = q.data;
  const d = r.ultimos30Dias;
  const linha = (i: ItemDoCalendario) => (
    <li key={`${i.publicacaoId}:${i.quando}`}>
      <button type="button" className="hugi-link-botao" onClick={() => props.aoAbrir(i.publicacaoId)}>{i.nome}</button>
      <small>{quando(i.quando)} · {contar(i.grupos, "grupo", "grupos")} · {i.conexaoApelido ?? i.conexaoNumero ?? "—"}</small>
      <SeloDoEstado estado={i.estado} />
    </li>
  );
  // A linha das últimas diz o RESULTADO, não só quantas saíram.
  const linhaP = (p: Publicacao) => (
    <li key={p.id}>
      <button type="button" className="hugi-link-botao" onClick={() => props.aoAbrir(p.id)}>{p.nome}</button>
      <small>
        {p.enviados} de {contar(p.alvos, "grupo", "grupos")}{p.falhados > 0 ? <span className="is-erro"> · {contar(p.falhados, "falhou", "falharam")}</span> : null}
        {" · "}{contar(p.reacoes, "reação", "reações")} · {contar(p.respostas, "resposta", "respostas")}{p.pausaMotivo ? ` · ${p.pausaMotivo}` : ""}
      </small>
      <SeloDoEstado estado={p.estado} />
    </li>
  );
  const fmt = (n: number) => n.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  const ins = r.insights;
  const dicaDoEngajamento = [
    "Reações + respostas + cliques sobre a audiência, nos últimos 30 dias.",
    ins.horarioComMaisAtividade ? `Horário com mais atividade: ${String(ins.horarioComMaisAtividade.hora).padStart(2, "0")}h (${fmt(ins.horarioComMaisAtividade.interacoesPorEntrega)} por entrega).` : null,
    ins.formatoComMaisAtividade ? `Formato com mais atividade: ${FORMATO[ins.formatoComMaisAtividade.formato] ?? ins.formatoComMaisAtividade.formato}.` : null,
    ins.gruposMaisResponsivos.length ? `Grupos mais responsivos: ${ins.gruposMaisResponsivos.map((g) => g.nome).join(", ")}.` : null,
    ins.nota,
  ].filter(Boolean).join(" ");

  const comAtencao = r.precisamDeAtencao.length > 0;
  // "Próximas" leva as de hoje no topo: duas listas com os mesmos itens confundiam (hoje ⊂ próximas).
  const vistos = new Set<string>();
  const proximas = [...r.hoje, ...r.proximas].filter((i) => {
    const k = `${i.publicacaoId}:${i.quando}`;
    if (vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });

  const metricas: MetricaDoHugi[] = [
    {
      id: "atencao", icone: <AlertTriangle size={16} />, tom: "neutro", rotulo: "Precisam de atenção", valor: r.precisamDeAtencao.length,
      detalhe: "falha ou sem conexão", ...(comAtencao ? { alerta: "perigo" as const, aoAbrir: () => setCartao("atencao") } : {}),
    },
    { id: "execucao", icone: <Send size={16} />, tom: "wa", rotulo: "Saindo agora", valor: r.emExecucao.length, detalhe: "em execução" },
    { id: "proximas", icone: <CalendarClock size={16} />, tom: "ag", rotulo: "Próximas", valor: proximas.length, detalhe: contar(r.hoje.length, "hoje", "hoje"), aoAbrir: () => setCartao("proximas") },
    { id: "grupos", icone: <UsersRound size={16} />, tom: "eq", rotulo: "Grupos ativos", valor: r.grupos.ativos, detalhe: `${r.grupos.conversacionais} conversacionais`, aoAbrir: () => props.aoIr("grupos") },
    {
      id: "sem-etiqueta", icone: <Tag size={16} />, tom: "neutro", rotulo: "Sem etiqueta", valor: r.grupos.semEtiqueta, detalhe: "nenhuma publicação os alcança por etiqueta",
      aoAbrir: () => props.aoIr("grupos"), ...(r.grupos.semEtiqueta > 0 ? { alerta: "atencao" as const } : {}),
    },
    {
      id: "engajamento", icone: <Heart size={16} />, tom: "hugi", rotulo: "Engajamento (30 dias)", valor: d.engajamento !== null ? porcento(d.engajamento) : null,
      detalhe: `${contar(d.publicacoes, "publicação", "publicações")} · ${d.grupos} grupos`, dica: dicaDoEngajamento,
    },
  ];

  const cartoes: CartaoDaGrade[] = [
    ...(comAtencao ? [{ id: "atencao", area: "atencao", titulo: "Precisam de atenção", rotuloCurto: "Atenção", contagem: r.precisamDeAtencao.length, conteudo: <ul className="hugi-linhas">{r.precisamDeAtencao.map(linhaP)}</ul> }] : []),
    {
      id: "proximas", area: "proximas", titulo: "Próximas", rotuloCurto: "Próximas", contagem: proximas.length,
      conteudo: proximas.length ? <ul className="hugi-linhas">{proximas.map(linha)}</ul> : <p className="hugi-grupos-nota hugi-linha-vazia">Nada agendado.</p>,
    },
    {
      id: "ultimas", area: "ultimas", titulo: "Últimas", rotuloCurto: "Últimas",
      conteudo: r.ultimas.length ? <ul className="hugi-linhas">{r.ultimas.map(linhaP)}</ul> : <p className="hugi-grupos-nota hugi-linha-vazia">Nenhuma concluída ainda.</p>,
    },
  ];

  return (
    <>
      <Metricas rotulo="Grupos agora" itens={metricas} />
      <GradeDeCartoes
        rotulo="Visão geral dos grupos"
        classe="hugi-grade-justa"
        cartoes={cartoes}
        {...(cartao ? { ativo: cartao, aoTrocar: setCartao } : {})}
        colunas={comAtencao ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))"}
        linhas="fit-content(100%)"
        areas={comAtencao ? '"atencao proximas ultimas"' : '"proximas ultimas"'}
      />
    </>
  );
}

// --- Grupos ------------------------------------------------------------------------------------------

const NOVA_ETIQUETA = "__nova__";

function TelaDeGrupos(props: { readonly modo: ReactNode }) {
  const sessao = useSessao();
  const cliente = useQueryClient();
  const podeSincronizar = sessao.permissoes.includes("sync_group");
  const podeEtiquetar = sessao.permissoes.includes("tag_group");
  const podeGerenciarPlano = sessao.permissoes.includes("manage_post");
  const [busca, setBusca] = useState("");
  const [etiqueta, setEtiqueta] = useState("");
  const [conexao, setConexao] = useState("");
  const q = useGrupos({ busca, etiqueta, conexao });
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [conexaoParaSincronizar, setConexaoParaSincronizar] = useState("");
  const [nomeEmLote, setNomeEmLote] = useState("");
  const [inicioDoLote, setInicioDoLote] = useState(1);
  const [conexaoDoLote, setConexaoDoLote] = useState("");
  const [renomeando, setRenomeando] = useState(false);
  const [resultadoDoLote, setResultadoDoLote] = useState<{ feitos: number; recusados: number; porGrupo: { grupo: string; ok: boolean; motivo: string | null }[] } | null>(null);

  const renomearEmLote = async (): Promise<void> => {
    setRenomeando(true);
    setResultadoDoLote(null);
    try {
      const r = await chamar<{ feitos: number; recusados: number; porGrupo: { grupo: string; ok: boolean; motivo: string | null }[] }>("/api/painel/grupos/lote", {
        metodo: "POST",
        corpo: { grupos: [...selecionados], conexaoId: conexaoDoLote, acao: "nome", texto: nomeEmLote, inicio: inicioDoLote },
      });
      setResultadoDoLote(r);
      await q.refetch();
    } finally {
      setRenomeando(false);
    }
  };
  const [sincronizando, setSincronizando] = useState(false);
  const [aviso, setAviso] = useState<{ tom: "ok" | "ruim"; texto: string } | null>(null);
  const [novaEtiqueta, setNovaEtiqueta] = useState("");
  const [criandoEtiqueta, setCriandoEtiqueta] = useState(false);

  const conexoes = q.data?.conexoes ?? [];
  useEffect(() => {
    if (!conexaoParaSincronizar && conexoes[0]) setConexaoParaSincronizar(conexoes[0].id);
  }, [conexoes, conexaoParaSincronizar]);

  if (q.isPending) return <div className="v2-cartao"><Carregando>Carregando os grupos…</Carregando></div>;
  if (q.isError) return <div className="v2-cartao"><Falha erro={q.error} aoTentarDeNovo={() => void q.refetch()} /></div>;
  const grupos = q.data.itens;
  const etiquetas = q.data.etiquetas;
  const daSincronizacao = conexoes.find((c) => c.id === conexaoParaSincronizar) ?? null;

  const sincronizar = async () => {
    if (!conexaoParaSincronizar) return;
    setSincronizando(true);
    setAviso(null);
    const r = await sincronizarGrupos(cliente, conexaoParaSincronizar);
    setSincronizando(false);
    if (r.ok) {
      const acima = r.dados.foraDoPlano > 0
        ? ` ${r.dados.foraDoPlano} ficaram FORA do plano (ele inclui ${r.dados.gruposIncluidos} grupos gerenciados; ${r.dados.gruposGerenciados} em uso) — troque por outro na lista para publicar neles.`
        : "";
      setAviso({ tom: "ok", texto: `${contar(r.dados.encontrados, "grupo encontrado", "grupos encontrados")}: ${contar(r.dados.novos, "novo", "novos")}, ${contar(r.dados.atualizados, "atualizado", "atualizados")}, ${r.dados.inativados} que ${r.dados.inativados === 1 ? "sumiu" : "sumiram"} desta conexão.${acima}` });
    }
    else setAviso({ tom: "ruim", texto: mensagemDa(r.falha) ?? "Não consegui sincronizar." });
  };
  const alternar = (id: string) => setSelecionados((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const aplicar = async (etiquetaId: string, modo: "adicionar" | "remover") => {
    if (selecionados.size === 0) return;
    const r = await etiquetarGrupos(cliente, [...selecionados], modo === "adicionar" ? [etiquetaId] : [], modo === "remover" ? [etiquetaId] : []);
    setAviso(r.ok ? { tom: "ok", texto: `${r.dados.aplicadas} aplicada(s), ${r.dados.removidas} removida(s).` } : { tom: "ruim", texto: mensagemDa(r.falha) ?? "Não consegui etiquetar." });
  };
  const criarEtiqueta = async () => {
    if (!novaEtiqueta.trim()) return;
    const r = await criarEtiquetaDeGrupo(cliente, novaEtiqueta.trim(), null);
    if (r.ok) { setNovaEtiqueta(""); setCriandoEtiqueta(false); }
    else setAviso({ tom: "ruim", texto: mensagemDa(r.falha) ?? "Não consegui criar a etiqueta." });
  };

  return (
    <>
      {/* A BARRA EM UMA LINHA (01/11, "confusa, desorganizada"): à esquerda o modo e os filtros, à direita o número e
          Sincronizar. Sem rótulo em cima de cada campo (o nome vai no `aria-label` e no placeholder), e a etiqueta nova
          nasce do próprio filtro de etiqueta ("+ Nova etiqueta…") — eram duas linhas de controles soltos. */}
      <section className="v2-cartao hugi-barra hugi-barra-dos-grupos" aria-label="Sincronizar e filtrar">
        {props.modo}
        <input className="campo" aria-label="Buscar" placeholder="Buscar grupo" value={busca} onChange={(e) => setBusca(e.target.value)} />
        {criandoEtiqueta ? (
          <span className="hugi-mini-acoes">
            <input className="campo" aria-label="Nova etiqueta" placeholder="Nome da etiqueta" autoFocus value={novaEtiqueta} onChange={(e) => setNovaEtiqueta(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void criarEtiqueta(); if (e.key === "Escape") setCriandoEtiqueta(false); }} />
            <Acao pequeno desabilitado={!novaEtiqueta.trim()} {...(novaEtiqueta.trim() ? {} : { motivo: "Escreva o nome da etiqueta." })} onClick={() => void criarEtiqueta()}>Criar etiqueta</Acao>
            <Acao pequeno onClick={() => setCriandoEtiqueta(false)}>Cancelar</Acao>
          </span>
        ) : (
          <select
            className="campo"
            aria-label="Etiqueta"
            value={etiqueta}
            onChange={(e) => { if (e.target.value === NOVA_ETIQUETA) setCriandoEtiqueta(true); else setEtiqueta(e.target.value); }}
          >
            <option value="">Todas as etiquetas</option>
            {etiquetas.map((e) => <option key={e.id} value={e.id}>{e.nome} ({e.grupos})</option>)}
            {podeEtiquetar ? <option value={NOVA_ETIQUETA}>+ Nova etiqueta…</option> : null}
          </select>
        )}
        <select className="campo" aria-label="Alcançado por" value={conexao} onChange={(e) => setConexao(e.target.value)}>
          <option value="">Qualquer conexão</option>
          {conexoes.map((c) => <option key={c.id} value={c.id}>{nomeDaConexao(c)}</option>)}
        </select>
        <span className="hugi-barra-direita">
          <select className="campo" aria-label="Conexão" value={conexaoParaSincronizar} onChange={(e) => setConexaoParaSincronizar(e.target.value)}
            title={`Última sincronização: ${daSincronizacao?.ultimaSincronizacaoEm ? quando(daSincronizacao.ultimaSincronizacaoEm) : "nunca"}${daSincronizacao && !daSincronizacao.disponivel ? ` · ${daSincronizacao.motivo}` : ""}`}>
            {conexoes.length === 0 ? <option value="">Nenhuma conexão da API Hugi</option> : null}
            {conexoes.map((c) => <option key={c.id} value={c.id}>{nomeDaConexao(c)}{c.disponivel ? "" : " (fora do ar)"}</option>)}
          </select>
          <Acao
            titulo={`Última sincronização: ${daSincronizacao?.ultimaSincronizacaoEm ? quando(daSincronizacao.ultimaSincronizacaoEm) : "nunca"}`}
            desabilitado={!podeSincronizar || !conexaoParaSincronizar || sincronizando}
            {...(!podeSincronizar ? { motivo: "Sincronizar exige a permissão sync_group." } : !conexaoParaSincronizar ? { motivo: "Conecte um número pela API Hugi em Canais." } : sincronizando ? { motivo: "Sincronizando…" } : {})}
            onClick={() => void sincronizar()}
          >
            {sincronizando ? "Sincronizando…" : "Sincronizar grupos"}
          </Acao>
        </span>
        {aviso ? <p role={aviso.tom === "ruim" ? "alert" : "status"} className={`hugi-barra-aviso${aviso.tom === "ruim" ? " is-ruim" : ""}`}>{aviso.texto}</p> : null}
      </section>

      {selecionados.size > 0 && podeEtiquetar ? (
        <section className="v2-cartao hugi-barra" aria-label="Ações em lote">
          <strong>{contar(selecionados.size, "grupo selecionado", "grupos selecionados")}:</strong>
          {etiquetas.map((e) => (
            <span key={e.id} className="hugi-mini-acoes">
              <Etiqueta nome={e.nome} cor={e.cor} className="px-2 py-0.5 text-[11px]" />
              <button type="button" className="hugi-link-botao" onClick={() => void aplicar(e.id, "adicionar")}>aplicar</button>
              <button type="button" className="hugi-link-botao is-fraco" onClick={() => void aplicar(e.id, "remover")}>tirar</button>
            </span>
          ))}
          <Acao pequeno onClick={() => setSelecionados(new Set())}>Limpar seleção</Acao>
        </section>
      ) : null}

      {/*
        RENOMEAR EM LOTE (H103, o item solto). `{{n}}` vira a posição do grupo na lista, começando no
        número escolhido — é o que faz "VIP {{n}}" virar VIP 1, VIP 2, VIP 3 sem digitar setenta nomes.
        O resultado volta POR GRUPO: um "70 renomeados" que esconde três recusas é inútil para quem
        administra setenta e não vai abrir os setenta para conferir.
      */}
      {selecionados.size > 0 && podeEtiquetar ? (
        <section className="v2-cartao hugi-barra" aria-label="Renomear em lote">
          <label>
            <span>Novo nome dos {selecionados.size} selecionados</span>
            <input className="campo" placeholder="Comunidade VIP {{n}}" value={nomeEmLote} onChange={(e) => setNomeEmLote(e.target.value)} />
          </label>
          <label>
            <span>Começar em</span>
            <input className="campo" type="number" min={0} value={inicioDoLote} onChange={(e) => setInicioDoLote(Number(e.target.value))} style={{ width: "5rem" }} />
          </label>
          <select className="campo" aria-label="Qual número administra" value={conexaoDoLote} onChange={(e) => setConexaoDoLote(e.target.value)}>
            <option value="">Qual número administra…</option>
            {conexoes.map((c) => <option key={c.id} value={c.id}>{nomeDaConexao(c)}</option>)}
          </select>
          <Acao
            desabilitado={renomeando || !nomeEmLote.trim() || !conexaoDoLote}
            {...(!nomeEmLote.trim() ? { motivo: "Escreva o novo nome." } : !conexaoDoLote ? { motivo: "Escolha o número que administra os grupos." } : {})}
            onClick={() => void renomearEmLote()}
          >
            Renomear
          </Acao>
          <Dica texto="{{n}} vira o número do grupo, na ordem da lista, começando no número escolhido." />
          {resultadoDoLote ? (
            <p className="hugi-barra-aviso">
              <strong>{resultadoDoLote.feitos} renomeado(s)</strong>
              {resultadoDoLote.porGrupo.filter((g) => !g.ok).map((g, i) => <span key={i} className="hugi-alerta"> · {g.grupo}: {g.motivo}</span>)}
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="v2-cartao hugi-cartao-elastico" aria-label="Grupos">
        <div className="hugi-corpo">
          {grupos.length === 0 ? (
            busca.trim() || etiqueta || conexao ? (
              // Com filtro ativo, "nenhum sincronizado" seria mentira: há grupos, nenhum casa. Medido em 05/09.
              <Vazio><strong>Nenhum grupo com este filtro</strong>{" "}<span>Limpe a busca, a etiqueta ou a conexão para ver a lista inteira.</span></Vazio>
            ) : (
              <Vazio><strong>Nenhum grupo sincronizado</strong>{" "}<span>Escolha a conexão e clique em Sincronizar grupos. A lista vem do WhatsApp do número; a tela lê daqui.</span></Vazio>
            )
          ) : (
            <table className="v2-tabela hugi-tabela-grupos">
              <thead>
                <tr>
                  <th scope="col"><input type="checkbox" aria-label="Selecionar todos" checked={grupos.every((g) => selecionados.has(g.id))} onChange={(e) => setSelecionados(e.target.checked ? new Set(grupos.map((g) => g.id)) : new Set())} /></th>
                  <th scope="col">Grupo</th><th scope="col" className="is-num">Participantes</th><th scope="col">Tipo</th><th scope="col">Etiquetas</th><th scope="col">Conexões</th><th scope="col">Analytics</th><th scope="col">Sincronizado</th><th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {grupos.map((g) => (
                  <LinhaDeGrupo
                    key={g.id}
                    g={g}
                    marcado={selecionados.has(g.id)}
                    aoMarcar={() => alternar(g.id)}
                    {...(podeGerenciarPlano
                      ? {
                          aoGerenciar: async (gerenciado: boolean) => {
                            const r = await gerenciarGrupo(cliente, g.id, gerenciado);
                            if (!r.ok) setAviso({ tom: "ruim", texto: mensagemDa(r.falha) ?? "Não consegui alterar o plano deste grupo." });
                          },
                          /*
                            A conexão usada é a PRIMEIRA ativa do grupo — administrar exige o socket, e
                            um grupo alcançado por dois números pode ser administrado por qualquer um
                            deles. Sem conexão ativa a ação nem é oferecida.
                          */
                          aoAdministrar: async (
                            acao: "nome" | "descricao" | "fechar" | "travar" | "convite",
                            extra?: { texto?: string; ligado?: boolean; regerar?: boolean },
                          ) => {
                            const cx = g.conexoes.find((c) => c.ativo);
                            if (!cx) {
                              setAviso({ tom: "ruim", texto: "Nenhuma conexão ativa alcança este grupo." });
                              return;
                            }
                            const r = await administrarGrupo(cliente, g.id, { conexaoId: cx.id, acao, ...(extra ?? {}) });
                            if (!r.ok) {
                              setAviso({ tom: "ruim", texto: mensagemDa(r.falha) ?? "Não consegui administrar este grupo." });
                              return;
                            }
                            setAviso({
                              tom: "ok",
                              texto: r.dados.link
                                ? `Link de convite: ${r.dados.link}`
                                : `Pronto: ${acao}.`,
                            });
                          },
                        }
                      : {})}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </>
  );
}

/**
 * CANAIS DO WHATSAPP — difusão sem grupo e sem teto de participantes.
 *
 * ## O aviso no topo não é enfeite
 *
 * Num canal **ninguém responde** — há reação e nada mais. Quem chega aqui esperando uma caixa de
 * entrada precisa saber disso antes de criar um canal e ficar esperando mensagem que não vem. A frase
 * curta fica visível; o resto mora no (i).
 *
 * ## `papel` decide o que a tela oferece
 *
 * Só o DONO publica num canal. Oferecer "publicar" para quem é seguidor seria oferecer um botão que
 * o servidor recusa — por isso a coluna existe e a tela a mostra.
 */
function TelaDeCanais(props: { readonly modo: ReactNode }) {
  const cliente = useQueryClient();
  const grupos = useGrupos({});
  const [conexao, setConexao] = useState("");
  const [canais, setCanais] = useState<CanalDoWhatsApp[] | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<{ tom: "ok" | "ruim"; texto: string } | null>(null);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [convite, setConvite] = useState("");

  const conexoes = grupos.data?.conexoes ?? [];
  useEffect(() => {
    if (!conexao && conexoes[0]) setConexao(conexoes[0].id);
  }, [conexoes, conexao]);

  const rodar = async (corpo: Parameters<typeof operarCanal>[1], sucesso: string) => {
    if (!conexao) {
      setAviso({ tom: "ruim", texto: "Escolha por qual número." });
      return;
    }
    setOcupado(true);
    setAviso(null);
    const r = await operarCanal(cliente, { ...corpo, conexaoId: conexao });
    setOcupado(false);
    if (!r.ok) {
      setAviso({ tom: "ruim", texto: mensagemDa(r.falha) ?? "Não consegui falar com os canais." });
      return;
    }
    if (r.dados.canais) setCanais(r.dados.canais);
    setAviso({ tom: "ok", texto: sucesso });
  };

  const lista = (
    <div className="hugi-corpo-registro">
      <p role="note" className="hugi-alerta">
        <strong>Ninguém responde num canal</strong> — há reação e nada mais, então nada daqui vira conversa no atendimento.
      </p>
      {aviso ? <p role="status" className={`hugi-alerta${aviso.tom === "ok" ? "" : " is-erro"}`}>{aviso.texto}</p> : null}
      {canais === null ? (
        <Vazio>Escolha o número e clique em Ver meus canais.</Vazio>
      ) : canais.length === 0 ? (
        <Vazio>Este número não segue nem tem canal nenhum.</Vazio>
      ) : (
        <div className="v2-tabela-rolagem">
          <table className="v2-tabela">
            <thead><tr><th scope="col">Canal</th><th scope="col" className="is-num">Seguidores</th><th scope="col">Papel</th><th scope="col">Ações</th></tr></thead>
            <tbody>
              {canais.map((c) => (
                <tr key={c.id}>
                  <td className="is-titulo">
                    <strong>{c.nome || c.id}</strong>
                    {c.descricao ? <small className="hugi-grupos-nota" style={{ display: "block" }}>{c.descricao}</small> : null}
                  </td>
                  <td className="is-num">{c.seguidores}</td>
                  <td>{c.papel}{c.silenciado ? " · silenciado" : ""}</td>
                  <td className="is-acoes">
                    <span className="hugi-mini-acoes">
                      <button type="button" className="hugi-link-botao"
                        onClick={() => void rodar({ acao: "silenciar", canal: c.id, ligado: !c.silenciado } as never, c.silenciado ? "Som ligado." : "Silenciado.")}>
                        {c.silenciado ? "tirar do silêncio" : "silenciar"}
                      </button>
                      <button type="button" className="hugi-link-botao"
                        onClick={() => void rodar({ acao: "deixar", canal: c.id } as never, "Você deixou o canal.")}>
                        deixar
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const cartoes: CartaoDaGrade[] = [
    {
      id: "canais", area: "lista", titulo: "Seus canais", rotuloCurto: "Seus canais",
      dica: "Um canal é difusão: só quem é dono publica, os seguidores não se veem, e não há teto de participantes.",
      ferramentas: (
        <span className="hugi-mini-acoes">
          {props.modo}
          <select className="campo" aria-label="Por qual número" value={conexao} onChange={(e) => setConexao(e.target.value)}>
            <option value="">{conexoes.length ? "Escolha" : "Nenhuma conexão da API Hugi"}</option>
            {conexoes.map((c) => (
              <option key={c.id} value={c.id}>
                {nomeDaConexao(c)}{c.disponivel ? "" : ` (fora do ar: ${c.motivo})`}
              </option>
            ))}
          </select>
          <Acao pequeno desabilitado={!conexao || ocupado} {...(!conexao ? { motivo: "Escolha o número." } : {})} onClick={() => void rodar({ acao: "listar" } as never, "Lista atualizada.")}>
            {ocupado ? "Consultando…" : "Ver meus canais"}
          </Acao>
        </span>
      ),
      conteudo: lista,
    },
    {
      id: "criar", area: "criar", titulo: "Criar um canal", rotuloCurto: "Criar",
      conteudo: (
        <div className="hugi-corpo-registro hugi-grupos-formulario">
          <label className="hugi-grupos-campo"><span>Nome do canal</span><input className="campo" placeholder="Nome do canal" value={nome} onChange={(e) => setNome(e.target.value)} /></label>
          <label className="hugi-grupos-campo"><span>Descrição</span><textarea className="campo" rows={2} placeholder="Descrição (opcional)" value={descricao} onChange={(e) => setDescricao(e.target.value)} /></label>
        </div>
      ),
      rodape: (
        <Acao
          tom="primaria"
          desabilitado={!conexao || !nome.trim() || ocupado}
          {...(!conexao ? { motivo: "Escolha o número." } : !nome.trim() ? { motivo: "Escreva o nome do canal." } : {})}
          onClick={() => void rodar({ acao: "criar", nome: nome.trim(), ...(descricao.trim() ? { descricao: descricao.trim() } : {}) } as never, "Canal criado.")}
        >
          Criar
        </Acao>
      ),
    },
    {
      id: "seguir", area: "seguir", titulo: "Seguir por convite", rotuloCurto: "Seguir",
      conteudo: (
        <div className="hugi-corpo-registro hugi-grupos-formulario">
          <label className="hugi-grupos-campo"><span>Convite</span><input className="campo" placeholder="https://whatsapp.com/channel/… ou só o código" value={convite} onChange={(e) => setConvite(e.target.value)} /></label>
        </div>
      ),
      rodape: (
        <Acao
          desabilitado={!conexao || !convite.trim() || ocupado}
          {...(!conexao ? { motivo: "Escolha o número." } : !convite.trim() ? { motivo: "Cole o convite." } : {})}
          onClick={() => void rodar({ acao: "seguir-por-convite", convite: convite.trim() } as never, "Seguindo.")}
        >
          Seguir
        </Acao>
      ),
    },
  ];

  return (
    <GradeDeCartoes
      rotulo="Canais do WhatsApp"
      classe="hugi-grade-justa"
      cartoes={cartoes}
      colunas="minmax(0, 2fr) minmax(0, 1fr)"
      linhas="minmax(0, 1fr) minmax(0, 1fr)"
      areas={'"lista criar" "lista seguir"'}
    />
  );
}

function LinhaDeGrupo(props: {
  readonly g: Grupo;
  readonly marcado: boolean;
  readonly aoMarcar: () => void;
  readonly aoGerenciar?: (gerenciado: boolean) => void | Promise<void>;
  readonly aoAdministrar?: (
    acao: "nome" | "descricao" | "fechar" | "travar" | "convite",
    extra?: { texto?: string; ligado?: boolean; regerar?: boolean },
  ) => Promise<void>;
}) {
  const { g } = props;
  const cap = g.capacidades as Partial<Record<"reacoes" | "respostasDiretas" | "mensagensDoGrupo" | "cliques", boolean>> & { recibos?: string };
  const observaveis = [
    cap.reacoes ? "Reações" : null,
    cap.respostasDiretas ? "Respostas" : null,
    cap.mensagensDoGrupo ? "Atividade pós-publicação" : null,
    cap.recibos && cap.recibos !== "nao" ? "Confirmações (parciais)" : null,
    cap.cliques ? "Cliques" : null,
  ].filter((x): x is string => x !== null);
  // Exportar busca a lista NO TRANSPORTE, na hora — não há cadastro guardado. Por isso precisa de
  // uma conexão viva, e some quando não há nenhuma.
  const viva = g.conexoes.find((c) => c.ativo);
  return (
    <tr className={g.ativo ? undefined : "hugi-linha-inativa"}>
      <td><input type="checkbox" aria-label={`Selecionar ${g.nome}`} checked={props.marcado} onChange={props.aoMarcar} /></td>
      <td className="is-titulo">
        <strong>{g.nome || g.idExterno}</strong>
        {!g.ativo ? <span style={{ display: "block" }}><Selo tom="perigo" dica="não apareceu na última sincronização">Inacessível</Selo></span> : null}
        {!g.gerenciado ? (
          <span className="hugi-mini-acoes" style={{ display: "flex" }}>
            <Selo tom="atencao" dica="não entra em publicação">Fora do plano</Selo>
            {props.aoGerenciar ? <button type="button" className="hugi-link-botao" onClick={() => void props.aoGerenciar?.(true)}>pôr no plano</button> : null}
          </span>
        ) : props.aoGerenciar ? (
          <button type="button" className="hugi-link-botao is-fraco" style={{ display: "block" }} onClick={() => void props.aoGerenciar?.(false)}>tirar do plano</button>
        ) : null}
      </td>
      <td className="is-num">
        {g.participantes}
        {viva ? (
          <a
            className="hugi-link-botao"
            style={{ marginLeft: 8 }}
            href={`/api/painel/grupos/${g.id}/participantes.csv?conexao=${encodeURIComponent(viva.id)}`}
            title="Baixa os telefones dos participantes deste grupo, buscados agora no WhatsApp"
          >
            exportar
          </a>
        ) : null}
      </td>
      <td>{TIPO_DE_GRUPO[g.permissaoDeMensagem] ?? g.permissaoDeMensagem}</td>
      <td><span className="hugi-etiquetas">{g.etiquetas.map((e) => <Etiqueta key={e.id} nome={e.nome} cor={e.cor} className="px-2 py-0.5 text-[11px]" />)}</span></td>
      <td>{g.conexoes.filter((c) => c.ativo).map((c) => `${nomeDaConexao({ ...c, apelido: c.apelido ?? null, numeroExibicao: c.numeroExibicao ?? null })}${c.somosAdmin ? " (admin)" : ""}`).join(", ") || "—"}</td>
      <td className="is-quebra">{observaveis.length ? observaveis.join(", ") : "—"}</td>
      <td>{quando(g.ultimaSincronizacaoEm)}</td>
      <td className="is-acoes">{props.aoAdministrar ? <AcoesDoGrupo grupo={g} aoAdministrar={props.aoAdministrar} /> : null}</td>
    </tr>
  );
}

/**
 * As ações que mexem NO GRUPO — e não na relação dele com o plano.
 *
 * ⚠️ **Cada uma pode falhar por não sermos administradores**, e quem sabe disso é o servidor do
 * WhatsApp. A resposta dele aparece como está: uma tradução nossa envelheceria, e uma checagem
 * prévia leria o cache, que pode estar velho.
 *
 * "Fechar" e "travar" são coisas diferentes e a confusão é fácil: **fechar** é só administradores
 * MANDAM MENSAGEM (o grupo de avisos); **travar** é só administradores EDITAM nome, foto e
 * descrição. O `title` de cada botão diz isso.
 */
function AcoesDoGrupo(props: {
  readonly grupo: Grupo;
  readonly aoAdministrar: (
    acao: "nome" | "descricao" | "fechar" | "travar" | "convite",
    extra?: { texto?: string; ligado?: boolean; regerar?: boolean },
  ) => Promise<void>;
}) {
  const g = props.grupo;
  const fechado = g.permissaoDeMensagem === "admins_only";
  return (
    <span className="hugi-mini-acoes">
      <button
        type="button"
        className="hugi-link-botao"
        onClick={() => {
          const novo = window.prompt("Novo nome do grupo", g.nome);
          if (novo?.trim()) void props.aoAdministrar("nome", { texto: novo.trim() });
        }}
      >
        renomear
      </button>
      <button
        type="button"
        className="hugi-link-botao"
        onClick={() => {
          const novo = window.prompt("Descrição do grupo (vazio apaga)", g.descricao ?? "");
          if (novo !== null) void props.aoAdministrar("descricao", { texto: novo });
        }}
      >
        descrição
      </button>
      <button
        type="button"
        title={fechado ? "Voltar a deixar todos publicarem" : "Só administradores publicam (grupo de avisos)"}
        className="hugi-link-botao"
        onClick={() => void props.aoAdministrar("fechar", { ligado: !fechado })}
      >
        {fechado ? "abrir" : "fechar"}
      </button>
      <button
        type="button"
        title="Link de convite (gera um novo e invalida o anterior)"
        className="hugi-link-botao"
        onClick={() => void props.aoAdministrar("convite")}
      >
        convite
      </button>
    </span>
  );
}

// --- Publicações --------------------------------------------------------------------------------------

const VISTAS: VistaDaLista<Publicacao>[] = [
  { id: "todas", rotulo: "Todas as publicações", passa: () => true },
  { id: "andamento", rotulo: "Em andamento", passa: (p) => ["agendada", "executando", "pausada", "aguardando_conexao", "freada"].includes(p.estado) },
  { id: "concluidas", rotulo: "Concluídas", passa: (p) => p.estado === "concluida" || p.estado === "parcialmente_concluida" },
  { id: "falhas", rotulo: "Com falha", passa: (p) => p.falhados > 0 || p.estado === "falhou" },
  { id: "canceladas", rotulo: "Canceladas", passa: (p) => p.estado === "cancelada" },
];

const tipoDaPublicacao = (p: Publicacao) => `${FORMATO[p.tipoDeConteudo] ?? p.tipoDeConteudo}${p.recorrencia ? " · recorrente" : ""}`;

/** Os dois modos de ver a aba Publicações: a lista e o calendário (cada um é um `?aba=`, para o link continuar valendo). */
function ModoDePublicacoes(props: { readonly ativo: "lista" | "calendario"; readonly aoTrocar: (aba: "publicacoes" | "calendario") => void }) {
  return (
    <ModoDaAba
      rotulo="Ver as publicações como"
      ativo={props.ativo}
      opcoes={[{ id: "lista", rotulo: "Lista", icone: <List size={14} aria-hidden="true" /> }, { id: "calendario", rotulo: "Calendário", icone: <CalendarDays size={14} aria-hidden="true" /> }]}
      aoTrocar={(id) => props.aoTrocar(id === "lista" ? "publicacoes" : "calendario")}
    />
  );
}

/** Os dois modos de "Seus grupos": os grupos e os canais do WhatsApp (difusão — não as conexões da tela Canais). */
function ModoDosGrupos(props: { readonly ativo: "grupos" | "canais"; readonly aoTrocar: (aba: "grupos" | "canais") => void }) {
  return (
    <ModoDaAba
      rotulo="Ver"
      ativo={props.ativo}
      opcoes={[{ id: "grupos", rotulo: "Grupos" }, { id: "canais", rotulo: "Canais do WhatsApp" }]}
      aoTrocar={props.aoTrocar}
    />
  );
}

function TelaDePublicacoes(props: { readonly aoAbrir: (id: string) => void; readonly modo: ReactNode }) {
  const q = usePublicacoes();
  if (q.isError) return <div className="v2-cartao"><Falha erro={q.error} aoTentarDeNovo={() => void q.refetch()} /></div>;
  // O nome leva à página da publicação — não há painel ao lado da lista.
  const colunas: ColunaDaLista<Publicacao>[] = [
    { id: "nome", rotulo: "Nome", principal: true, link: true, celula: (p) => p.nome, dica: (p) => p.nome, ordenar: (p) => p.nome },
    { id: "tipo", rotulo: "Tipo", celula: tipoDaPublicacao, ordenar: tipoDaPublicacao },
    { id: "estado", rotulo: "Estado", celula: (p) => <SeloDoEstado estado={p.estado} />, ordenar: (p) => ESTADO[p.estado].rotulo },
    {
      id: "enviados", rotulo: "Enviados", numerica: true,
      celula: (p) => <span className={p.falhados > 0 ? "hugi-negativo" : undefined}>{p.enviados}/{p.alvos}</span>,
      dica: (p) => `${contar(p.enviados, "enviado", "enviados")} de ${contar(p.alvos, "grupo", "grupos")}${p.falhados > 0 ? ` · ${contar(p.falhados, "falhou", "falharam")}` : ""}`,
      ordenar: (p) => p.enviados,
    },
    { id: "reacoes", rotulo: "Reações", numerica: true, celula: (p) => p.reacoes, ordenar: (p) => p.reacoes },
    { id: "respostas", rotulo: "Respostas", numerica: true, celula: (p) => p.respostas, ordenar: (p) => p.respostas },
    { id: "engajamento", rotulo: "Engajamento", numerica: true, celula: (p) => (p.engajamento !== null ? porcento(p.engajamento) : "—"), ordenar: (p) => p.engajamento },
    { id: "criada", rotulo: "Criada", celula: (p) => dataComHora(p.criadaEm), ordenar: (p) => p.criadaEm },
    { id: "fim", rotulo: "Fim", celula: (p) => dataComHora(p.concluidaEm ?? p.canceladaEm), ordenar: (p) => p.concluidaEm ?? p.canceladaEm },
  ];
  return (
    <ListView<Publicacao>
      titulo="Publicações"
      icone={<Send size={18} />}
      colunas={colunas}
      linhas={q.data?.itens as Publicacao[] | undefined}
      chave={(p) => p.id}
      vistas={VISTAS}
      memoria="hugi:publicacoes"
      abrir={{ href: (p) => `/publicacoes/${encodeURIComponent(p.id)}`, aoAbrir: (p) => props.aoAbrir(p.id) }}
      busca={{ campos: (p) => [p.nome, tipoDaPublicacao(p), p.conexaoApelido, p.conexaoNumero, p.texto], placeholder: "Nome, número ou texto" }}
      contagem={(n) => (n === 1 ? "1 publicação" : `${n} publicações`)}
      acoes={props.modo}
      vazio={{
        semNada: <><strong>Nenhuma publicação ainda</strong>{" "}<span>Sincronize os grupos, etiquete, e crie a primeira em Nova publicação. A prévia diz para quantos grupos ela sai.</span></>,
        semResultado: "Nenhuma publicação nesta vista.",
      }}
    />
  );
}

const ID_DO_TITULO = "hugi-publicacao-titulo";

/**
 * A página de UMA publicação (`/publicacoes/<id>`): o que ela é, as ações, as execuções, o resultado,
 * as entregas por grupo, os grupos congelados e quem fez o quê. A lista fica em `/publicacoes?aba=publicacoes`.
 */
export function PublicacaoDetalhe(props: { readonly id: string }) {
  const sessao = useSessao();
  const podeGerenciar = sessao.permissoes.includes("manage_post");
  const [, navegar] = useLocation();
  const lista = usePublicacoes();
  const conexoes: readonly ConexaoParaPublicar[] = lista.data?.conexoes ?? [];
  const cliente = useQueryClient();
  const q = usePublicacao(props.id);
  const [execucaoId, setExecucaoId] = useState<string | null>(null);
  const entregas = useEntregasDaPublicacao(props.id, execucaoId);
  const analise = useAnaliseDaPublicacao(props.id, execucaoId);
  const [erro, setErro] = useState<string | null>(null);
  const [novaConexao, setNovaConexao] = useState("");
  // No rodízio, QUAL número sai (0097). Vazio = o principal.
  const [sai, setSai] = useState("");
  const voltar = () => navegar("/publicacoes?aba=publicacoes");
  const casca = (titulo: string, corpo: ReactNode, dica?: string) => (
    <PaginaInteira rotulo={titulo}>
      <CabecalhoDaTela icone={<Send size={18} />} titulo={titulo} {...(dica ? { dica } : {})} secundarias={<Acao onClick={voltar}>Todas as publicações</Acao>} />
      <div className="v2-cartao">{corpo}</div>
    </PaginaInteira>
  );
  if (q.isPending) return casca("Publicação", <Carregando>Carregando a publicação…</Carregando>);
  if (q.isError) {
    // 404 não é "tentar de novo": o link está errado ou a publicação foi apagada. Página própria, como a campanha.
    if (falhaDe(q.error)?.tipo === "nao_encontrado") return casca("Publicação não encontrada", <Vazio>Ela pode ter sido apagada, ou o link está errado.</Vazio>);
    return casca("Publicação", <Falha erro={q.error} aoTentarDeNovo={() => void q.refetch()} />);
  }
  const { publicacao: p, execucoes, alvos, eventos, remetentes } = q.data;
  // O RODÍZIO DE CHIPS (0097): a publicação sai por mais de um número.
  const rodizio = (p.remetentesExtras?.length ?? 0) > 0;
  const atuais = new Set([p.conexaoId, ...(p.remetentesExtras ?? []).map((r) => r.id)]);
  const viva = ["agendada", "executando", "pausada", "aguardando_conexao", "freada"].includes(p.estado);
  const agir = async (acao: "cancelar" | "pausar" | "retomar") => {
    if (acao === "cancelar" && !window.confirm("Cancelar esta publicação? O que ainda não saiu não sai mais.")) return;
    setErro(null);
    const r = await agirNaPublicacao(cliente, p.id, acao);
    if (!r.ok) setErro(mensagemDa(r.falha) ?? "Não foi possível.");
  };
  const trocar = async () => {
    if (!novaConexao) return;
    setErro(null);
    const r = await trocarRemetenteDaPublicacao(cliente, p.id, novaConexao, rodizio ? (sai || p.conexaoId) : null);
    if (!r.ok) setErro(mensagemDa(r.falha) ?? "Não foi possível trocar o remetente.");
  };
  const quandoSai = p.recorrencia
    ? `${p.recorrencia.dias.map((d) => DIAS[d]).join(", ")} às ${p.recorrencia.horarios.join(", ")} até ${p.recorrencia.fim}`
    : quando(p.agendadaPara);
  const a = analise.data ?? null;
  // A telemetria por grupo casa com a entrega pelo id — uma linha por grupo, entrega e resultado juntos.
  const porEntrega = new Map((a?.entregas ?? []).map((e) => [e.entregaId, e]));
  const itens = entregas.data?.itens ?? [];
  const temErro = itens.some((e) => e.erro);
  // VARIAÇÕES DE TEXTO (0095): com mais de uma versão, cada grupo diz qual recebeu.
  const temVersoes = (p.variacoes?.length ?? 0) > 0;
  const execucaoAtiva = execucaoId ?? null;

  const acoes: AcaoDoRegistro[] = [
    ...(podeGerenciar && viva
      ? [
          p.estado === "pausada" || p.estado === "aguardando_conexao"
            ? { id: "retomar", rotulo: "Retomar", tom: "primaria" as const, aoClicar: () => void agir("retomar") }
            : { id: "pausar", rotulo: "Pausar", aoClicar: () => void agir("pausar") },
          { id: "cancelar", rotulo: "Cancelar", tom: "perigo" as const, aoClicar: () => void agir("cancelar") },
        ]
      : []),
  ];

  const alerta = p.estado === "aguardando_conexao" || p.estado === "pausada" || p.estado === "freada" ? (
    <p role="alert" className="hugi-alerta">
      {p.estado === "aguardando_conexao" ? `A execução está esperando a conexão: ${p.pausaMotivo ?? "o número não está no ar"}. Ela retoma sozinha quando o mesmo número voltar — ou troque o remetente no cartão Envio.` : p.estado === "freada" ? `Pausada pelo freio: ${p.pausaMotivo ?? "o WhatsApp restringiu este número"}. Volta sozinha quando passar.` : `Pausada${p.pausaMotivo ? `: ${p.pausaMotivo}` : ""}.`}
    </p>
  ) : null;
  const rodape = alerta || erro ? <>{alerta}{erro ? <p role="alert" className="hugi-alerta is-erro">{erro}</p> : null}</> : null;

  // 1. Os números, numa linha, antes de qualquer tabela — é o que se abre a página para ver.
  const faixa: Indicador[] = [
    {
      id: "enviados", rotulo: "Enviados",
      valor: <>{(a?.enviados ?? p.enviados).toLocaleString("pt-BR")} <small className="hugi-grupos-nota">de {a?.gruposSelecionados ?? p.alvos}</small></>,
      detalhe: `${a?.falhados ?? p.falhados} ${(a?.falhados ?? p.falhados) === 1 ? "falhou" : "falharam"}`,
    },
    { id: "audiencia", rotulo: "Audiência potencial", valor: a ? a.audienciaPotencial.toLocaleString("pt-BR") : null, detalhe: "participantes no envio" },
    {
      id: "unica", rotulo: "Audiência única", valor: a ? <Valor m={a.audienciaUnica} /> : null,
      ...(a ? { detalhe: a.audienciaUnica.disponivel ? (a.sobreposicao.valor ? `${porcento(a.sobreposicao.valor)} em mais de um grupo` : "ninguém em mais de um grupo") : a.audienciaUnica.motivo ?? undefined } : {}),
    },
    {
      id: "engajamento", rotulo: "Engajamento",
      valor: a ? (a.taxaDeEngajamento.disponivel && a.taxaDeEngajamento.valor !== null ? porcento(a.taxaDeEngajamento.valor) : <Valor m={a.taxaDeEngajamento} />) : null,
      ...(a ? {
        detalhe: a.uplift.disponivel && a.uplift.valor !== null
          ? <span title={a.uplift.motivo ?? undefined} className={a.uplift.valor < 0 ? "hugi-negativo" : undefined}>{sinalPorcento(a.uplift.valor)} sobre as últimas</span>
          : <span title={a.uplift.motivo ?? undefined}>{a.taxaDeEngajamento.disponivel ? "da audiência potencial" : a.taxaDeEngajamento.motivo ?? undefined}</span>,
      } : {}),
    },
    {
      id: "reacoes", rotulo: "Reações", valor: a ? <Valor m={a.reacoes} /> : null,
      ...(a ? {
        detalhe: a.reacoesPorEmoji.length > 0
          ? <span title={a.reacoesPorEmoji.map((r) => `${r.emoji} ${r.quantas} (${porcento(r.fatia)})`).join(" · ")}>{a.reacoesPorEmoji.slice(0, 3).map((r) => `${r.emoji} ${r.quantas}`).join(" · ")}{a.reacoesPorEmoji.length > 3 ? ` +${a.reacoesPorEmoji.length - 3}` : ""}</span>
          : a.reacoes.motivo ?? undefined,
      } : {}),
    },
    { id: "respostas", rotulo: "Respostas diretas", valor: a ? <Valor m={a.respostas} /> : null, detalhe: a?.respostas.motivo ?? undefined },
    { id: "mensagens", rotulo: "Mensagens em 24h", valor: a ? <Valor m={a.mensagensApos24h} /> : null, detalhe: a?.mensagensApos24h.motivo ?? undefined },
    { id: "pessoas", rotulo: "Pessoas que interagiram", valor: a ? <Valor m={a.participantesDistintos} /> : null, detalhe: a?.participantesDistintos.motivo ?? undefined },
    {
      id: "primeira", rotulo: "Até a 1ª interação",
      valor: a?.segundosAteAPrimeiraInteracao.disponivel && a.segundosAteAPrimeiraInteracao.valor !== null ? duracao(a.segundosAteAPrimeiraInteracao.valor) : a ? <Valor m={a.segundosAteAPrimeiraInteracao} /> : null,
      detalhe: a?.segundosAteAPrimeiraInteracao.motivo ?? undefined,
    },
    { id: "cliques", rotulo: "Cliques", valor: a ? <Valor m={a.cliques} /> : null, detalhe: a?.cliques.motivo ?? undefined },
  ].map((i) => (i.detalhe === undefined ? (({ detalhe: _sem, ...resto }) => resto)(i) : i)) as Indicador[];

  return (
    <PaginaDeRegistro
      idDoTitulo={ID_DO_TITULO}
      trilha={[{ rotulo: TITULO, href: "/publicacoes?aba=publicacoes", aoClicar: voltar }, { rotulo: p.nome }]}
      rotulos={{ principal: "Publicação", lateral: "Quem fez o quê" }}
      destaque={
        <Destaque
          icone={<Send size={18} />}
          tipo="Publicação em grupos"
          titulo={p.nome}
          idDoTitulo={ID_DO_TITULO}
          campos={[
            { rotulo: "Estado", valor: <SeloDoEstado estado={p.estado} /> },
            { rotulo: "Quando", valor: p.recorrencia ? quandoSai : `uma vez, ${quando(p.agendadaPara)}`, dica: quandoSai },
            { rotulo: "Formato", valor: FORMATO[p.tipoDeConteudo] ?? p.tipoDeConteudo },
            { rotulo: "Grupos escolhidos", valor: alvos.length },
            { rotulo: "Execuções", valor: execucoes.length },
          ]}
          acoes={<BarraDeAcoes acoes={acoes} />}
          {...(rodape ? { rodape } : {})}
        />
      }
      caminho={<PainelDeIndicadores faixa rotulo="Resultado da publicação" indicadores={faixa} />}
      principal={
        <section className="v2-cartao hugi-cartao-elastico" aria-label="Publicação">
          <div className="hugi-corpo">
            <div className="hugi-corpo-registro">
              {/*
                NÚMEROS DO RODÍZIO (0097). A execução continua `executando` enquanto um número espera — sem esta
                tabela, "parou pela metade" não teria explicação na tela. DEPOIS da faixa de números.
              */}
              {rodizio && (remetentes?.length ?? 0) > 0 ? (
                <section className="v2-cartao" aria-label="Números do rodízio">
                  <header className="hugi-cartao-cabecalho"><h3>Números do rodízio</h3></header>
                  <div className="v2-tabela-rolagem">
                    <table className="v2-tabela hugi-tabela-pequena">
                      <thead>
                        <tr>
                          <th scope="col">Número</th>
                          <th scope="col">Agora</th>
                          <th scope="col" className="is-num">Enviadas</th>
                          <th scope="col" className="is-num">Esperando</th>
                          <th scope="col" className="is-num">Falhas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(remetentes ?? []).map((r) => {
                          const agora = estadoDoRemetente(r);
                          return (
                            <tr key={r.conexaoId}>
                              <td className="is-titulo">
                                <strong>{rotuloDoNumero(r)}</strong>
                                {r.principal ? <small>principal</small> : null}
                              </td>
                              <td>
                                <Selo tom={TOM[agora.tom]}>{agora.rotulo}</Selo>
                                {agora.motivo ? <small>{agora.motivo}</small> : null}
                              </td>
                              <td className="is-num">{r.enviadas}</td>
                              <td className="is-num">{r.pendentes}</td>
                              <td className="is-num">{r.falhadas}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {/* 2. Três cartões lado a lado, na mesma altura: o que foi mandado, a atenção nas 24 h, para quem. */}
              <div className="hugi-tres">
                <section className="v2-cartao hugi-secao">
                  <h3>Envio</h3>
                  {p.texto && (p.variacoes?.length ?? 0) > 0 ? (
                    [p.texto, ...(p.variacoes ?? [])].map((t, i) => (
                      <div key={i}>
                        <small className="hugi-grupos-nota">{`Versão ${i + 1}`}</small>
                        <p className="hugi-mensagem">{t}</p>
                      </div>
                    ))
                  ) : p.texto ? <p className="hugi-mensagem">{p.texto}</p> : null}
                  {p.midiaId && p.tipoDeConteudo === "imagem" ? <img src={`/api/painel/publicacoes/midias/${p.midiaId}`} alt="" style={{ maxHeight: "12rem", maxWidth: "100%", objectFit: "contain", borderRadius: 8 }} /> : null}
                  {p.midiaId && p.tipoDeConteudo !== "imagem" ? <a className="hugi-link-botao" href={`/api/painel/publicacoes/midias/${p.midiaId}`} target="_blank" rel="noreferrer">Abrir o arquivo ({FORMATO[p.tipoDeConteudo]})</a> : null}
                  <dl className="hugi-pares">
                    <div><dt>Por</dt><dd>{rodizio ? `${1 + (p.remetentesExtras?.length ?? 0)} números` : (p.conexaoApelido ?? p.conexaoNumero ?? "—")} <small className="hugi-grupos-nota">· {p.ritmoPorMinuto} grupos/min{rodizio ? " cada" : ""}</small></dd></div>
                    <div><dt>Quando</dt><dd>{p.recorrencia ? quandoSai : `uma vez, ${quando(p.agendadaPara)}`}</dd></div>
                    <div><dt>Links rastreáveis</dt><dd>{p.rastrearLinks ? "Sim" : "Não"}</dd></div>
                    <div><dt>Avisou todo mundo</dt><dd>{p.mencionarTodos ? "Sim" : "Não"}</dd></div>
                  </dl>
                  {podeGerenciar && viva ? (
                    <div className="hugi-troca">
                      <strong>Trocar o remetente</strong>
                      {rodizio ? (
                        <select aria-label="Qual número sai" className="campo" value={sai || p.conexaoId} onChange={(e) => setSai(e.target.value)}>
                          {(remetentes ?? []).map((r) => <option key={r.conexaoId} value={r.conexaoId}>{`Sai: ${rotuloDoNumero(r)}`}</option>)}
                        </select>
                      ) : null}
                      {/* Todos os números conectados já estão na publicação: uma caixa só com "Escolha" seria controle morto. */}
                      {conexoes.some((c) => !atuais.has(c.id)) ? (
                        <span>
                          <select aria-label="Número que entra" className="campo" value={novaConexao} onChange={(e) => setNovaConexao(e.target.value)}>
                            <option value="">Escolha outra conexão</option>
                            {conexoes.filter((c) => !atuais.has(c.id)).map((c) => <option key={c.id} value={c.id}>{nomeDaConexao(c)}{c.disponivel ? "" : " (fora do ar)"}</option>)}
                          </select>
                          <Acao pequeno desabilitado={!novaConexao} {...(novaConexao ? {} : { motivo: "Escolha a conexão." })} onClick={() => void trocar()}>Trocar</Acao>
                        </span>
                      ) : (
                        <small className="hugi-grupos-nota">Nenhum outro número conectado para entrar.</small>
                      )}
                    </div>
                  ) : null}
                </section>

                <section className="v2-cartao hugi-secao" aria-label="Atenção nas 24 horas">
                  <h3>Atenção nas 24 h{a ? <small className="hugi-grupos-nota" title={a.meiaVidaSegundos.motivo ?? undefined}> · meia-vida {a.meiaVidaSegundos.disponivel && a.meiaVidaSegundos.valor !== null ? duracao(a.meiaVidaSegundos.valor) : "N/A"}</small> : null}</h3>
                  {a ? (
                    <>
                      {/* A curva: interações por hora desde o envio. Barra vazia é zero medido; a curva só existe com amostra. */}
                      <div className="hugi-curva" role="img" aria-label={`Interações por hora nas 24 h: ${a.curva24h.join(", ")}`}>
                        {a.curva24h.map((n, h) => {
                          const maior = Math.max(1, ...a.curva24h);
                          return <span key={h} title={`${h}ª hora: ${n}`} style={{ height: `${Math.max(n > 0 ? 8 : 2, Math.round((n / maior) * 100))}%`, opacity: n > 0 ? 1 : 0.25 }} />;
                        })}
                      </div>
                      <div className="hugi-curva-eixo"><span>envio</span><span>12 h</span><span>24 h</span></div>
                      <dl className="hugi-pares" style={{ marginTop: 8 }}>
                        <div title={a.saldoDeParticipantes.motivo ?? undefined}>
                          <dt>Entraram / saíram</dt>
                          <dd>{a.saldoDeParticipantes.disponivel ? <>+{a.entradas24h.valor ?? 0} / −{a.saidas24h.valor ?? 0} <strong className={(a.saldoDeParticipantes.valor ?? 0) < 0 ? "hugi-negativo" : undefined}>({sinal(a.saldoDeParticipantes.valor ?? 0)})</strong></> : <Valor m={a.saldoDeParticipantes} />}</dd>
                        </div>
                        <div title={a.concentracao.motivo ?? undefined}>
                          <dt>Os 10% mais ativos fizeram</dt>
                          <dd><Valor m={a.concentracao} formatar={porcento} /></dd>
                        </div>
                        <div title={a.encaminhamentos.motivo ?? undefined}>
                          <dt>Texto reencaminhado</dt>
                          <dd>{a.encaminhamentos.disponivel && a.encaminhamentos.valor !== null
                            ? (a.encaminhamentos.valor === 0 ? "0" : `${a.encaminhamentos.valor}× em ${a.gruposQueEncaminharam} grupo${a.gruposQueEncaminharam === 1 ? "" : "s"}${a.encaminhamentosForaDosAlvos > 0 ? ` · ${a.encaminhamentosForaDosAlvos} fora dos escolhidos` : ""}`)
                            : <Valor m={a.encaminhamentos} />}</dd>
                        </div>
                      </dl>
                    </>
                  ) : <Carregando />}
                </section>

                <section className="v2-cartao hugi-secao">
                  <h3>Grupos escolhidos <small className="hugi-grupos-nota">· {alvos.length}, congelados ao agendar</small></h3>
                  <ul className="hugi-congelados">
                    {alvos.map((g) => <li key={g.grupoId}>{g.nome} <small className="hugi-grupos-nota">· {g.participantes}</small></li>)}
                  </ul>
                </section>
              </div>

              {/*
                POR VERSÃO DO TEXTO (0095) — o que o cliente pediu quando falou em "mensagens revezadas".
                Números, não veredito: a linha diz "pouco para comparar" quando a versão saiu em poucos
                grupos, e a tela não escolhe uma vencedora.
              */}
              {(a?.porVariacao?.length ?? 0) > 0 ? (
                <section className="v2-cartao">
                  <header className="hugi-cartao-cabecalho"><h3>Por versão do texto</h3></header>
                  <div className="v2-tabela-rolagem">
                    <table className="v2-tabela hugi-tabela-pequena">
                      <thead>
                        <tr>
                          <th scope="col">Versão</th>
                          <th scope="col" className="is-num">Grupos</th>
                          <th scope="col" className="is-num">Reações</th>
                          <th scope="col" className="is-num" title="Respostas diretas">Resp.</th>
                          <th scope="col" className="is-num" title="Reações e respostas por grupo">Por grupo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(a?.porVariacao ?? []).map((v) => (
                          <tr key={v.versao}>
                            <td className="is-titulo">
                              <strong>{`Versão ${v.versao}`}</strong>
                              <small title={v.texto} className="hugi-grupos-texto">{v.texto}</small>
                            </td>
                            <td className="is-num">{v.grupos}</td>
                            <td className="is-num">{v.reacoes}</td>
                            <td className="is-num"><Valor m={v.respostas} /></td>
                            <td className="is-num">
                              {v.interacoesPorGrupo === null ? "—" : v.interacoesPorGrupo.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}
                              {v.amostraPequena && v.grupos > 0 ? <small>pouco para comparar</small> : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {/* O RESULTADO POR NÚMERO do rodízio (0097): a mesma régua da versão do texto — números, não veredito. */}
              {(a?.porRemetente?.length ?? 0) > 0 ? (
                <section className="v2-cartao">
                  <header className="hugi-cartao-cabecalho"><h3>Por número</h3></header>
                  <div className="v2-tabela-rolagem">
                    <table className="v2-tabela hugi-tabela-pequena">
                      <thead>
                        <tr>
                          <th scope="col">Número</th>
                          <th scope="col" className="is-num">Grupos</th>
                          <th scope="col" className="is-num">Reações</th>
                          <th scope="col" className="is-num" title="Respostas diretas">Resp.</th>
                          <th scope="col" className="is-num" title="Reações e respostas por grupo">Por grupo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(a?.porRemetente ?? []).map((r) => (
                          <tr key={r.conexaoId}>
                            <td className="is-titulo"><strong>{rotuloDoNumero(r)}</strong></td>
                            <td className="is-num">{r.grupos}</td>
                            <td className="is-num">{r.reacoes}</td>
                            <td className="is-num"><Valor m={r.respostas} /></td>
                            <td className="is-num">
                              {r.interacoesPorGrupo === null ? "—" : r.interacoesPorGrupo.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}
                              {r.amostraPequena && r.grupos > 0 ? <small>pouco para comparar</small> : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {/* 3. Uma tabela por grupo: entrega e resultado na mesma linha. */}
              <section className="v2-cartao">
                <header className="hugi-cartao-cabecalho">
                  <h3>Por grupo</h3>
                  {execucoes.length > 0 ? (
                    <ul className="hugi-chips" aria-label="Execuções">
                      {execucoes.length > 1 ? (
                        <li><button type="button" className="hugi-chip" aria-pressed={execucaoAtiva === null} onClick={() => setExecucaoId(null)}>Todas as execuções</button></li>
                      ) : null}
                      {execucoes.map((x) => (
                        <li key={x.id}>
                          <button type="button" className="hugi-chip" aria-pressed={execucaoAtiva === x.id || execucoes.length === 1}
                            title={`${contar(x.enviados, "enviado", "enviados")} · ${contar(x.falhados, "falhado", "falhados")} · ${contar(x.pendentes, "pendente", "pendentes")}${x.motivo ? ` · ${x.motivo}` : ""}`}
                            onClick={() => setExecucaoId(execucoes.length === 1 ? null : x.id)}>
                            {quando(x.previstaPara)} · {(ESTADO[x.estado as EstadoDePublicacao]?.rotulo ?? x.estado).toLowerCase()}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </header>
                {itens.length ? (
                  <div className="v2-tabela-rolagem">
                    <table className="v2-tabela hugi-tabela-pequena">
                      <thead>
                        <tr>
                          <th scope="col">Grupo</th>{temVersoes ? <th scope="col">Versão</th> : null}{rodizio ? <th scope="col">Número</th> : null}<th scope="col">Entrega</th><th scope="col">Horário</th>
                          <th scope="col" className="is-num" title="Participantes no envio">Partic.</th><th scope="col" className="is-num">Reações</th><th scope="col" className="is-num" title="Respostas diretas">Resp.</th>
                          <th scope="col" className="is-num">Msgs 24h</th><th scope="col" className="is-num">Entregue</th><th scope="col" className="is-num">Lida</th><th scope="col" className="is-num" title="Entradas menos saídas nas 24 h">Saldo</th>
                          {temErro ? <th scope="col">Erro</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {itens.map((e) => {
                          const t = porEntrega.get(e.id);
                          const estado = e.estado === "enviada" ? { tom: "sucesso" as const, rotulo: "Enviada" } : e.estado === "falhou" ? { tom: "perigo" as const, rotulo: `Falhou (${e.tentativas}x)` } : e.estado === "cancelada" ? { tom: "perigo" as const, rotulo: "Cancelada" } : e.estado === "enviando" ? { tom: "sucesso" as const, rotulo: "Enviando" } : { tom: "neutro" as const, rotulo: "Pendente" };
                          return (
                            <tr key={e.id} title={e.idExternoDaMensagem ? `Id da mensagem: ${e.idExternoDaMensagem}` : undefined}>
                              <td className="is-titulo"><strong>{e.grupoNome}</strong>{t ? <small>{TIPO_DE_GRUPO[t.permissaoDeMensagem] ?? t.permissaoDeMensagem}</small> : null}</td>
                              {temVersoes ? <td className="is-num">{e.variacao === null || e.variacao === undefined ? "—" : e.variacao + 1}</td> : null}
                              {rodizio ? <td>{e.conexaoApelido ?? (e.conexaoNumero ? identificadorLegivel(e.conexaoNumero) : "—")}</td> : null}
                              <td><Selo tom={estado.tom}>{estado.rotulo}</Selo></td>
                              <td>{horaCurta(e.enviadaEm ?? e.previstaPara)}</td>
                              <td className="is-num">{e.participantesNoEnvio ?? "—"}</td>
                              <td className="is-num">{t ? <Valor m={t.reacoes} /> : "—"}</td>
                              <td className="is-num">{t ? <Valor m={t.respostas} /> : "—"}</td>
                              <td className="is-num">{t ? <Valor m={t.mensagens24h} /> : "—"}</td>
                              <td className="is-num">{t ? <Valor m={t.confirmaramEntrega} /> : "—"}</td>
                              <td className="is-num">{t ? <Valor m={t.confirmaramLeitura} /> : "—"}</td>
                              <td className={`is-num${t && (t.saidas24h.valor ?? 0) > (t.entradas24h.valor ?? 0) ? " hugi-negativo" : ""}`} title={t ? `+${t.entradas24h.valor ?? 0} entraram, −${t.saidas24h.valor ?? 0} saíram nas 24 h` : undefined}>{t ? sinal((t.entradas24h.valor ?? 0) - (t.saidas24h.valor ?? 0)) : "—"}</td>
                              {temErro ? <td className="hugi-negativo is-quebra">{e.erro ?? ""}</td> : null}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : entregas.isPending ? <Carregando>Carregando as entregas…</Carregando> : <Vazio>Nenhuma entrega ainda.</Vazio>}
              </section>
            </div>
          </div>
        </section>
      }
      lateral={
        <Atividade
          titulo="Quem fez o quê"
          vazio="Nenhum registro."
          eventos={eventos.map((e, i) => ({
            id: `${i}:${e.ocorridoEm}`,
            quando: e.ocorridoEm,
            titulo: e.tipo,
            detalhe: `${e.atorNome ?? e.atorId ?? "sistema"}${e.motivo ? ` · ${e.motivo}` : ""}`,
            tom: "neutro" as const,
          }))}
        />
      }
    />
  );
}

// --- Calendário -------------------------------------------------------------------------------------

function Calendario(props: { readonly aoAbrir: (id: string) => void; readonly modo: ReactNode }) {
  const [mes, setMes] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const de = useMemo(() => new Date(mes.getFullYear(), mes.getMonth(), 1 - 7).toISOString(), [mes]);
  const ate = useMemo(() => new Date(mes.getFullYear(), mes.getMonth() + 1, 7).toISOString(), [mes]);
  const q = useCalendarioDePublicacoes(de, ate);
  const [visao, setVisao] = useState<"mes" | "lista">("mes");
  const cabecalho = (
    <header className="hugi-cartao-cabecalho">
      <Acao pequeno onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))}>‹ Mês anterior</Acao>
      <span className="hugi-calendario-mes">{mes.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })}</span>
      <Acao pequeno onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))}>Próximo mês ›</Acao>
      {/* "Mês | Agenda" é a visão DENTRO do calendário; "Lista | Calendário" (à direita) troca o modo da aba. */}
      <span className="hugi-mini-acoes" style={{ marginLeft: "auto" }} role="group" aria-label="Visão do calendário">
        <button type="button" className={`v2-botao is-pequeno${visao === "mes" ? " is-ligado" : ""}`} aria-pressed={visao === "mes"} onClick={() => setVisao("mes")}>Mês</button>
        <button type="button" className={`v2-botao is-pequeno${visao === "lista" ? " is-ligado" : ""}`} aria-pressed={visao === "lista"} onClick={() => setVisao("lista")}>Agenda</button>
      </span>
      {props.modo}
    </header>
  );
  if (q.isPending) return <section className="v2-cartao hugi-cartao-elastico" aria-label="Calendário">{cabecalho}<Carregando>Carregando o calendário…</Carregando></section>;
  if (q.isError) return <section className="v2-cartao hugi-cartao-elastico" aria-label="Calendário">{cabecalho}<Falha erro={q.error} aoTentarDeNovo={() => void q.refetch()} /></section>;
  const itens = q.data.itens;
  const porDia = new Map<string, ItemDoCalendario[]>();
  for (const i of itens) {
    const d = new Date(i.quando);
    const chave = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    porDia.set(chave, [...(porDia.get(chave) ?? []), i]);
  }
  const primeiro = new Date(mes.getFullYear(), mes.getMonth(), 1);
  const inicioDaGrade = new Date(primeiro.getTime() - primeiro.getDay() * 86_400_000);
  const dias = Array.from({ length: 42 }, (_, i) => new Date(inicioDaGrade.getTime() + i * 86_400_000));
  const item = (i: ItemDoCalendario) => (
    <button key={`${i.publicacaoId}:${i.quando}`} type="button" onClick={() => props.aoAbrir(i.publicacaoId)} title={`${i.nome} · ${contar(i.grupos, "grupo", "grupos")} · ${i.conexaoApelido ?? i.conexaoNumero ?? ""} · ${ESTADO[i.estado as EstadoDePublicacao]?.rotulo ?? i.estado}`}
      className={`hugi-calendario-item${i.prevista ? " is-prevista" : ""}`}>
      {new Date(i.quando).toLocaleTimeString("pt-BR", { timeStyle: "short" })} {i.nome}
    </button>
  );
  return (
    <section className="v2-cartao hugi-cartao-elastico" aria-label="Calendário">
      {cabecalho}
      <div className="hugi-corpo">
        {visao === "mes" ? (
          <div className="hugi-calendario" style={{ height: "100%", minHeight: "22rem" }}>
            {DIAS.map((d) => <div key={d} className="hugi-calendario-dow">{d}</div>)}
            {dias.map((d) => {
              const chave = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              const doMes = d.getMonth() === mes.getMonth();
              return (
                <div key={chave} className={`hugi-calendario-dia${doMes ? "" : " is-fora"}`}>
                  <span>{d.getDate()}</span>
                  {(porDia.get(chave) ?? []).map(item)}
                </div>
              );
            })}
          </div>
        ) : itens.length === 0 ? (
          <Vazio>Nada neste período.</Vazio>
        ) : (
          <ul className="hugi-linhas hugi-secao">
            {itens.map((i) => (
              <li key={`${i.publicacaoId}:${i.quando}`}>
                <button type="button" className="hugi-link-botao" onClick={() => props.aoAbrir(i.publicacaoId)}>{i.nome}</button>
                <small>{quando(i.quando)} · {contar(i.grupos, "grupo", "grupos")} · {i.conexaoApelido ?? i.conexaoNumero ?? "—"}</small>
                <SeloDoEstado estado={i.estado} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// --- Nova publicação ----------------------------------------------------------------------------------

const hojeIso = () => new Date().toISOString().slice(0, 10);

/**
 * Enter num campo de uma linha NÃO envia o cadastro: o envio aqui agenda publicações em grupos, e antes
 * da v2 não havia formulário — Enter não fazia nada. O `<form>` é do kit; a tecla fica presa aqui.
 */
const semEnterQueEnvia = (e: KeyboardEvent<HTMLDivElement>) => {
  if (e.key === "Enter" && (e.target as HTMLElement).tagName === "INPUT") e.preventDefault();
};

/** A espera entre a última mudança e o pedido da prévia (a mesma da campanha). */
const ESPERA_DA_PREVIA_MS = 500;
/** No corpo da prévia, "agora" é um marcador: trocado pelo instante só na hora de pedir, senão a chave mudaria a cada render. */
const AGORA = "__agora__";

type Formato = "texto" | "imagem" | "video" | "documento" | "enquete" | "botoes";
const ICONE_DO_FORMATO: Readonly<Record<Formato, ReactNode>> = {
  texto: <Type size={18} />, imagem: <ImageIcon size={18} />, video: <Video size={18} />,
  documento: <FileText size={18} />, enquete: <ListChecks size={18} />, botoes: <MousePointerClick size={18} />,
};
const PASSOS = [
  { n: 1, rotulo: "Conteúdo" },
  { n: 2, rotulo: "Grupos" },
  { n: 3, rotulo: "Quando e por qual número" },
] as const;

/*
  NOVA PUBLICAÇÃO EM TRÊS PASSOS (spec 23, jornada 5 — aprovada pelo owner em 01/11): Conteúdo → Grupos → Quando e por
  qual número, com o RESUMO FIXO ao lado (a bolha da mensagem, quantos grupos, quando, por qual número) e UMA primária,
  "Agendar em N grupos".

  ⚠️ POR QUE ACABOU O "REVISAR": até 01/11 a primária era "Revisar" até a prévia chegar — e a prévia nascia no fim do
  formulário de 1.924 px, FORA DA VISTA (`Publicacoes.tsx:1470,1638` da época): clicar em Revisar só trocava o rótulo
  do botão. Agora a prévia se pede sozinha ~500 ms depois de qualquer mudança (a mesma rota, `POST
  /publicacoes/previa`), o resultado mora no resumo, que está sempre à vista, e o botão diz para quantos grupos vai.
  Enquanto o corpo está incompleto, a frase do servidor ("Escreva o texto", "Escolha pelo menos um grupo") é o motivo
  do botão desabilitado.

  Os três passos ficam MONTADOS (só o ativo aparece): pular entre eles não perde nada, e o resumo lê de todos.
  O número único é pré-escolhido; o rodízio de chips e o ritmo moram em "Avançado", no passo 3.
*/
function NovaPublicacao(props: { readonly aoFechar: (id: string | null) => void }) {
  const cliente = useQueryClient();
  const grupos = useGrupos({});
  const base = useId();
  const [passo, setPasso] = useState<1 | 2 | 3>(1);
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState<Formato>("texto");
  // Uma linha por opção/rótulo — a forma mais direta de editar uma lista curta sem inventar widget.
  const [opcoes, setOpcoes] = useState("");
  const [escolhas, setEscolhas] = useState("1");
  const [rodape, setRodape] = useState("");
  const [texto, setTexto] = useState("");
  // As versões EXTRAS do texto (0095). O campo "Texto" é a versão 1.
  const [variacoes, setVariacoes] = useState<string[]>([]);
  const [midia, setMidia] = useState<{ id: string; nome: string; tipo: string } | null>(null);
  const [conexaoId, setConexaoId] = useState("");
  // O RODÍZIO DE CHIPS (0097): os números que revezam com o principal, na ordem em que foram marcados.
  const [revezarCom, setRevezarCom] = useState<string[]>([]);
  const [gruposEscolhidos, setGruposEscolhidos] = useState<Set<string>>(new Set());
  const [etiquetasEscolhidas, setEtiquetasEscolhidas] = useState<Set<string>>(new Set());
  // Sem padrão (como a campanha, D7): a pessoa diz se sai agora, numa data ou se repete.
  const [modo, setModo] = useState<"agora" | "data" | "recorrente" | null>(null);
  const [quandoCampo, setQuandoCampo] = useState("");
  const [dias, setDias] = useState<Set<number>>(new Set([1, 2, 3, 4, 5]));
  const [horarios, setHorarios] = useState("09:00");
  const [inicio, setInicio] = useState(hojeIso);
  const [fim, setFim] = useState(() => new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10));
  const [rastrear, setRastrear] = useState(false);
  const [mencionar, setMencionar] = useState(false);
  // Ritmo em grupos por minuto — a mesma unidade da campanha. 30 = um grupo a cada 2 s.
  const [ritmo, setRitmo] = useState("30");
  const [busca, setBusca] = useState("");
  const [previa, setPrevia] = useState<PreviaDaPublicacao | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [erroDaPrevia, setErroDaPrevia] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [subindo, setSubindo] = useState(false);
  const [agendando, setAgendando] = useState(false);

  const conexoes = grupos.data?.conexoes ?? [];
  useEffect(() => { if (!conexaoId && conexoes.length === 1 && conexoes[0]) setConexaoId(conexoes[0].id); }, [conexoes, conexaoId]);

  const linhas = (v: string) => v.split("\n").map((x) => x.trim()).filter(Boolean);
  // Onde variar faz sentido: na enquete o texto é a pergunta; nos botões, o corpo de uma estrutura.
  const podeVariar = tipo === "texto" || tipo === "imagem" || tipo === "video" || tipo === "documento";
  const versoesPreenchidas = podeVariar ? variacoes.map((x) => x.trim()).filter(Boolean) : [];
  const extrasDoRodizio = revezarCom.filter((id) => id !== conexaoId && conexoes.some((c) => c.id === id));
  const rotuloDoRemetente = (id: string) => { const c = conexoes.find((x) => x.id === id); return c ? nomeDaConexao(c) : "um número"; };
  const corpo = (agendadaParaAgora: string) => ({
    nome, tipoDeConteudo: tipo, texto, midiaId: temArquivo(tipo) ? (midia?.id ?? null) : null, conexaoId,
    ...(extrasDoRodizio.length > 0 ? { remetentesExtras: extrasDoRodizio } : {}),
    ...(tipo === "enquete" ? { opcoes: linhas(opcoes), escolhas: Number(escolhas) || 1 } : {}),
    ...(tipo === "botoes" ? { botoes: linhas(opcoes), rodape: rodape.trim() || null } : {}),
    grupos: [...gruposEscolhidos], etiquetas: [...etiquetasEscolhidas], rastrearLinks: rastrear, mencionarTodos: mencionar,
    ritmoPorMinuto: ritmo.trim() === "" ? undefined : Number(ritmo),
    ...(versoesPreenchidas.length > 0 ? { variacoes: versoesPreenchidas } : {}),
    ...(modo === "recorrente"
      ? { recorrencia: { dias: [...dias], horarios: horarios.split(/[,\s]+/).filter(Boolean), inicio, fim, fuso: Intl.DateTimeFormat().resolvedOptions().timeZone } }
      // Sem escolha ainda, a prévia conta os grupos como se saísse agora; o botão é que espera a escolha.
      : { agendadaPara: modo === "data" ? (quandoCampo ? new Date(quandoCampo).toISOString() : null) : agendadaParaAgora }),
  });

  // A prévia lê tudo menos o nome. "Agora" entra como marcador e vira o instante só no pedido.
  const chaveDaPrevia = JSON.stringify({ ...corpo(AGORA), nome: undefined });
  useEffect(() => {
    setPrevia(null);
    setErroDaPrevia(null);
    setCalculando(true);
    let viva = true;
    const espera = setTimeout(() => {
      const pedido = JSON.parse(chaveDaPrevia) as Record<string, unknown>;
      if (pedido["agendadaPara"] === AGORA) pedido["agendadaPara"] = new Date().toISOString();
      void previaDePublicacao(pedido).then((r) => {
        if (!viva) return; // o corpo mudou enquanto o pedido voava
        setCalculando(false);
        if (r.ok) setPrevia(r.dados);
        else setErroDaPrevia(mensagemDa(r.falha) ?? "Não foi possível montar a prévia.");
      });
    }, ESPERA_DA_PREVIA_MS);
    return () => { viva = false; clearTimeout(espera); };
  }, [chaveDaPrevia]);

  const agendar = async () => {
    setAgendando(true); setErro(null);
    const r = await criarPublicacao(cliente, corpo(new Date().toISOString()));
    setAgendando(false);
    if (r.ok) props.aoFechar(r.dados.id); else setErro(mensagemDa(r.falha) ?? "Não foi possível agendar.");
  };
  const subir = async (arquivo: File | undefined) => {
    if (!arquivo) return;
    setSubindo(true); setErro(null);
    const r = await guardarMidiaDePublicacao(arquivo);
    setSubindo(false);
    if (r.ok) { setMidia({ id: r.dados.id, nome: r.dados.nome, tipo: r.dados.tipo }); setTipo(r.dados.tipo); }
    else setErro(mensagemDa(r.falha) ?? "Não consegui enviar o arquivo.");
  };
  const alternar = <T,>(s: Set<T>, v: T) => { const n = new Set(s); if (n.has(v)) n.delete(v); else n.add(v); return n; };
  const lista = (grupos.data?.itens ?? []).filter((g) => g.ativo && (!busca || g.nome.toLowerCase().includes(busca.toLowerCase())));

  if (grupos.isPending || grupos.isError) {
    return (
      <PaginaInteira rotulo="Nova publicação">
        <CabecalhoDaTela icone={<Send size={18} />} titulo="Nova publicação" secundarias={<Acao onClick={() => props.aoFechar(null)}>Voltar</Acao>} />
        <div className="v2-cartao">{grupos.isError ? <Falha erro={grupos.error} aoTentarDeNovo={() => void grupos.refetch()} /> : <Carregando>Carregando os grupos…</Carregando>}</div>
      </PaginaInteira>
    );
  }

  const motivo = subindo
    ? "Enviando o arquivo…"
    : calculando || (!previa && !erroDaPrevia)
      ? "Contando os grupos…"
      : erroDaPrevia
        ? erroDaPrevia
        : previa && previa.grupos === 0
          ? "Nenhum grupo alcançado por esta conexão."
          : modo === null
            ? "Escolha quando: agora, numa data ou recorrente."
            : null;
  const verbo = modo === "agora" ? "Enviar agora" : "Agendar";
  const rotuloDaPrimaria = previa && !calculando ? `${verbo} em ${contar(previa.grupos, "grupo", "grupos")}` : verbo;

  const quandoNoResumo = modo === null ? "—"
    : modo === "agora" ? "Agora"
      : modo === "data" ? (quandoCampo ? quando(new Date(quandoCampo).toISOString()) : "—")
        : `${[...dias].sort().map((d) => DIAS[d]).join(", ") || "—"} · ${horarios}`;
  const corpoDaBolha = texto.trim() || (tipo === "enquete" ? "A pergunta da enquete" : temArquivo(tipo) ? "" : "O texto aparece aqui.");
  const passoId = (n: number) => `${base}-passo-${n}`;

  return (
    <PaginaDeCadastro
      icone={<Send size={18} />}
      titulo="Nova publicação"
      dica={FRASE}
      trilha={[{ rotulo: TITULO, href: "/publicacoes", aoClicar: () => props.aoFechar(null) }]}
      rotuloDaPrimaria={rotuloDaPrimaria}
      aoSalvar={() => void agendar()}
      aoCancelar={() => props.aoFechar(null)}
      rotuloDoCancelar="Voltar"
      salvando={agendando}
      motivo={agendando ? null : motivo}
      erro={erro}
    >
      <div className="hugi-pub-nova" onKeyDown={semEnterQueEnvia}>
        <div className="hugi-pub-passos-area">
          <ol className="hugi-pub-passos" aria-label="Passos da publicação">
            {PASSOS.map((p) => (
              <li key={p.n}>
                <button type="button" aria-current={passo === p.n ? "step" : undefined} aria-controls={passoId(p.n)} onClick={() => setPasso(p.n)}>
                  <span className="hugi-pub-passo-n" aria-hidden="true">{p.n}</span>{p.rotulo}
                </button>
              </li>
            ))}
          </ol>

          <section id={passoId(1)} hidden={passo !== 1} aria-label="Conteúdo" className="hugi-pub-passo v2-cadastro-campos">
            <CampoDoCadastro rotulo="Nome">
              {(p) => <input {...p} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Vídeo 04/09 — manhã" />}
            </CampoDoCadastro>
            <div className="v2-cadastro-campo is-largo">
              <span className="hugi-grupos-nota">Formato</span>
              <span className="hugi-formatos" role="radiogroup" aria-label="Formato">
                {(["texto", "imagem", "video", "documento", "enquete", "botoes"] as const).map((t) => (
                  <label key={t} className={`hugi-formato${tipo === t ? " is-ligado" : ""}`}>
                    <input type="radio" name={`${base}-tipo`} className="v2-sr" checked={tipo === t} onChange={() => setTipo(t)} />
                    <span aria-hidden="true">{ICONE_DO_FORMATO[t]}</span>{FORMATO[t]}
                  </label>
                ))}
              </span>
            </div>
            {temArquivo(tipo) ? (
              <CampoDoCadastro largo rotulo={`Arquivo (${tipo === "imagem" ? "jpeg, png, webp, até 8 MB" : tipo === "video" ? "mp4, até 16 MB" : "pdf, office, txt, csv, zip, até 16 MB"})`} {...(midia ? { ajuda: `Enviado: ${midia.nome}` } : {})}>
                {(p) => <input {...p} type="file" accept={tipo === "imagem" ? "image/*" : tipo === "video" ? "video/*" : ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"} onChange={(e) => void subir(e.target.files?.[0])} />}
              </CampoDoCadastro>
            ) : null}
            <CampoDoCadastro largo rotulo={tipo === "enquete" ? "Pergunta" : tipo === "botoes" ? "Texto (é ele que aparece se o botão não desenhar)" : temArquivo(tipo) ? (variacoes.length > 0 ? "Legenda — versão 1" : "Legenda (opcional)") : variacoes.length > 0 ? "Texto — versão 1" : "Texto"}>
              {(p) => (
                <textarea
                  {...p}
                  rows={4}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  placeholder={tipo === "enquete" ? "Qual o melhor horário?" : temArquivo(tipo) ? "Uma legenda para acompanhar o arquivo" : "O que sai nos grupos"}
                />
              )}
            </CampoDoCadastro>
            {podeVariar ? (
              <div className="v2-cadastro-campo is-largo">
                {variacoes.map((v, i) => (
                  <div key={i} className="hugi-grupos-campo">
                    <span className="hugi-mini-acoes" style={{ justifyContent: "space-between", display: "flex" }}>
                      {`Versão ${i + 2}`}
                      <button type="button" className="hugi-link-botao is-fraco" onClick={() => setVariacoes(variacoes.filter((_, j) => j !== i))}>Tirar</button>
                    </span>
                    <textarea
                      className="campo"
                      rows={3}
                      aria-label={`Versão ${i + 2} do texto`}
                      value={v}
                      onChange={(e) => { const nova = [...variacoes]; nova[i] = e.target.value; setVariacoes(nova); }}
                    />
                  </div>
                ))}
                {variacoes.length < 9 ? (
                  <span><Acao pequeno onClick={() => setVariacoes([...variacoes, ""])}>Outra versão do texto</Acao></span>
                ) : null}
              </div>
            ) : null}
            {tipo === "enquete" || tipo === "botoes" ? (
              <CampoDoCadastro largo rotulo={tipo === "enquete" ? "Opções (uma por linha, de 2 a 12)" : "Botões (um por linha, até 3)"}>
                {(p) => (
                  <textarea
                    {...p}
                    rows={3}
                    value={opcoes}
                    onChange={(e) => setOpcoes(e.target.value)}
                    placeholder={tipo === "enquete" ? "9h\n14h\n18h" : "Sim\nNão"}
                  />
                )}
              </CampoDoCadastro>
            ) : null}
            {tipo === "enquete" ? (
              <CampoDoCadastro rotulo="Quantas opções cada pessoa pode marcar">
                {(p) => <input {...p} type="number" min={1} max={12} value={escolhas} onChange={(e) => setEscolhas(e.target.value)} />}
              </CampoDoCadastro>
            ) : null}
            {tipo === "botoes" ? (
              <CampoDoCadastro rotulo="Rodapé (opcional)">
                {(p) => <input {...p} value={rodape} onChange={(e) => setRodape(e.target.value)} placeholder="responda até sexta" />}
              </CampoDoCadastro>
            ) : null}
            <label className="hugi-marcar"><input type="checkbox" checked={rastrear} onChange={(e) => setRastrear(e.target.checked)} />Trocar os links do texto por links rastreáveis (contam cliques por grupo, sem identificar pessoas)</label>
            {/* O rótulo diz o que a caixinha FAZ e o que ela não faz: notificar não é escrever `@`,
                e quem espera ver o arroba na mensagem concluiria que não funcionou. */}
            <label className="hugi-marcar"><input type="checkbox" checked={mencionar} onChange={(e) => setMencionar(e.target.checked)} />Avisar todo mundo do grupo (notifica cada membro no celular; não escreve @ na mensagem)</label>
            <div className="hugi-pub-passo-pe"><Acao onClick={() => setPasso(2)}>Continuar: grupos</Acao></div>
          </section>

          <section id={passoId(2)} hidden={passo !== 2} aria-label="Grupos" className="hugi-pub-passo v2-cadastro-campos">
            {/* Etiqueta primeiro: é como quem tem 40 grupos pensa ("os de lideranças"). Grupo a grupo vem depois. */}
            {grupos.data.etiquetas.length ? (
              <div className="v2-cadastro-campo is-largo">
                <span className="hugi-grupos-nota">Por etiqueta</span>
                <div className="hugi-opcoes">
                  {grupos.data.etiquetas.map((e) => (
                    <label key={e.id}><input type="checkbox" checked={etiquetasEscolhidas.has(e.id)} onChange={() => setEtiquetasEscolhidas((s) => alternar(s, e.id))} /><Etiqueta nome={`${e.nome} (${e.grupos})`} cor={e.cor} className="px-2 py-0.5 text-[11px]" /></label>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="v2-cadastro-campo is-largo">
              <span className="hugi-grupos-nota">Ou grupo a grupo</span>
              <input className="campo" aria-label="Buscar grupo pelo nome" placeholder="Buscar grupo pelo nome" value={busca} onChange={(e) => setBusca(e.target.value)} />
              <div className="hugi-lista-de-grupos">
                {lista.length === 0 ? <p className="hugi-grupos-nota">Nenhum grupo. Sincronize na aba Seus grupos.</p> : null}
                {lista.map((g) => (
                  <label key={g.id}>
                    <input type="checkbox" checked={gruposEscolhidos.has(g.id)} onChange={() => setGruposEscolhidos((s) => alternar(s, g.id))} />
                    <span>{g.nome}</span>
                    <small className="hugi-grupos-nota">{g.participantes} · {TIPO_DE_GRUPO[g.permissaoDeMensagem]}</small>
                  </label>
                ))}
              </div>
            </div>
            <div className="hugi-pub-passo-pe"><Acao onClick={() => setPasso(3)}>Continuar: quando</Acao></div>
          </section>

          <section id={passoId(3)} hidden={passo !== 3} aria-label="Quando e por qual número" className="hugi-pub-passo v2-cadastro-campos">
            <div className="hugi-opcoes" role="radiogroup" aria-label="Quando a publicação sai" style={{ gridColumn: "1 / -1" }}>
              <label><input type="radio" name={`${base}-modo`} checked={modo === "agora"} onChange={() => setModo("agora")} />Agora</label>
              <label><input type="radio" name={`${base}-modo`} checked={modo === "data"} onChange={() => setModo("data")} />Escolher data</label>
              <label><input type="radio" name={`${base}-modo`} checked={modo === "recorrente"} onChange={() => setModo("recorrente")} />Recorrente</label>
            </div>
            {modo === "data" ? (
              <CampoDoCadastro rotulo="Data e hora">
                {(p) => <input {...p} type="datetime-local" value={quandoCampo} onChange={(e) => setQuandoCampo(e.target.value)} />}
              </CampoDoCadastro>
            ) : null}
            {modo === "recorrente" ? (
              <>
                <div className="v2-cadastro-campo is-largo">
                  <span className="hugi-grupos-nota">Dias da semana</span>
                  <span className="hugi-opcoes">
                    {DIAS.map((d, i) => <label key={d}><input type="checkbox" checked={dias.has(i)} onChange={() => setDias((s) => alternar(s, i))} />{d}</label>)}
                    <button type="button" className="hugi-link-botao" onClick={() => setDias(new Set([1, 2, 3, 4, 5]))}>dias úteis</button>
                  </span>
                </div>
                <CampoDoCadastro rotulo="Horários (HH:MM, separados por vírgula)">
                  {(p) => <input {...p} value={horarios} onChange={(e) => setHorarios(e.target.value)} placeholder="09:00, 17:00" />}
                </CampoDoCadastro>
                <CampoDoCadastro rotulo="De">
                  {(p) => <input {...p} type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />}
                </CampoDoCadastro>
                <CampoDoCadastro rotulo="Até">
                  {(p) => <input {...p} type="date" value={fim} onChange={(e) => setFim(e.target.value)} />}
                </CampoDoCadastro>
              </>
            ) : null}
            <CampoDoCadastro rotulo="Enviar por">
              {(p) => (
                <select {...p} value={conexaoId} onChange={(e) => setConexaoId(e.target.value)}>
                  <option value="">{conexoes.length ? "Escolha a conexão" : "Nenhuma conexão da API Hugi — conecte em Canais"}</option>
                  {conexoes.map((c) => <option key={c.id} value={c.id}>{nomeDaConexao(c)}{c.disponivel ? "" : ` (fora do ar: ${c.motivo})`}</option>)}
                </select>
              )}
            </CampoDoCadastro>
            <details className="hugi-avancado">
              <summary>Avançado</summary>
              <div className="v2-cadastro-campos">
                <CampoDoCadastro rotulo="Ritmo (grupos por minuto)" ajuda="30 é um grupo a cada 2 segundos; 6 é um a cada 10.">
                  {(p) => <input {...p} inputMode="numeric" value={ritmo} onChange={(e) => setRitmo(e.target.value.replace(/\D/g, ""))} />}
                </CampoDoCadastro>
                {conexaoId && conexoes.length > 1 ? (
                  <fieldset className="v2-cadastro-campo is-largo hugi-opcoes" style={{ border: 0, margin: 0, padding: 0 }}>
                    <legend className="hugi-grupos-nota">Revezar com outros números</legend>
                    {conexoes.filter((c) => c.id !== conexaoId).map((c) => {
                      const marcado = revezarCom.includes(c.id);
                      // Até 9 além do principal — o teto do servidor (MAXIMO_DE_REMETENTES_EXTRAS) e da CHECK da 0097.
                      return (
                        <label key={c.id}>
                          <input type="checkbox" checked={marcado} disabled={!marcado && extrasDoRodizio.length >= 9}
                            onChange={() => setRevezarCom((xs) => (xs.includes(c.id) ? xs.filter((x) => x !== c.id) : [...xs, c.id]))} />
                          {nomeDaConexao(c)}{c.disponivel ? "" : " (fora do ar)"}
                        </label>
                      );
                    })}
                  </fieldset>
                ) : null}
              </div>
            </details>
          </section>
        </div>

        {/* O RESUMO FIXO: o que vai sair, para onde, quando e por qual número — sempre à vista, recalculado sozinho. */}
        <aside className="hugi-pub-resumo" aria-label="Resumo da publicação">
          <div className="hugi-bolha-area">
            <div className="hugi-bolha">
              {temArquivo(tipo) ? (
                <span className="hugi-bolha-arquivo">{ICONE_DO_FORMATO[tipo]}<span>{midia?.nome ?? `${FORMATO[tipo]} (envie o arquivo)`}</span></span>
              ) : null}
              {corpoDaBolha ? <p className={texto.trim() ? undefined : "is-fraco"}>{corpoDaBolha}</p> : null}
              {tipo === "enquete" ? <ul className="hugi-bolha-opcoes">{linhas(opcoes).map((o) => <li key={o}>○ {o}</li>)}</ul> : null}
              {tipo === "botoes" && rodape.trim() ? <small>{rodape.trim()}</small> : null}
              {tipo === "botoes" ? <span className="hugi-bolha-botoes">{linhas(opcoes).map((o) => <span key={o}>{o}</span>)}</span> : null}
            </div>
          </div>
          <div role="status" aria-live="polite" className="hugi-pub-resumo-numeros">
            <dl className="hugi-pares">
              <div><dt>Grupos</dt><dd>{calculando ? "contando…" : previa ? <strong>{contar(previa.grupos, "grupo", "grupos")}</strong> : "—"}</dd></div>
              <div><dt>Audiência</dt><dd>{previa && !calculando ? `${previa.audienciaPotencial.toLocaleString("pt-BR")} participantes` : "—"}</dd></div>
              <div><dt>Quando</dt><dd>{quandoNoResumo}</dd></div>
              <div><dt>Por</dt><dd>{conexaoId ? `${rotuloDoRemetente(conexaoId)}${extrasDoRodizio.length ? ` + ${extrasDoRodizio.length} no rodízio` : ""}` : "—"}</dd></div>
            </dl>
            {previa && !calculando ? (
              <>
                <small>
                  {modo === "recorrente" && previa.ocorrencias > 1 ? `${previa.ocorrencias} envio(s): ${previa.proximasOcorrencias.map(quando).join(", ")}${previa.ocorrencias > previa.proximasOcorrencias.length ? "…" : ""}. ` : ""}
                  {`A ${contar(previa.ritmoPorMinuto, "grupo", "grupos")} por minuto, cada envio leva ${duracao(previa.duracaoDeCadaEnvioS)} do primeiro ao último grupo.`}
                  {versoesPreenchidas.length > 0 ? ` ${1 + versoesPreenchidas.length} versões do texto, revezadas entre os grupos.` : ""}
                  {previa.gruposIncluidos !== null ? ` O plano inclui até ${previa.gruposIncluidos} grupos por publicação.` : ""}
                  {previa.linksNoTexto > 0 ? ` ${previa.linksNoTexto} link(s) no texto${rastrear ? (previa.rastreioDisponivel ? " serão rastreados." : " NÃO serão rastreados (sem URL pública).") : "."}` : ""}
                </small>
                {/* O RODÍZIO (0097) em linha própria: dentro da frase acima ele fazia a linha quebrar (bancada, 11/09). */}
                {(previa.porRemetente?.length ?? 0) > 0 ? (
                  <small>{`Revezando entre ${previa.porRemetente!.length} números: ${previa.porRemetente!.map((r) => `${rotuloDoRemetente(r.conexaoId)}, ${contar(r.grupos, "grupo", "grupos")}`).join(" · ")}.`}</small>
                ) : null}
                {previa.avisos.map((a, i) => <p key={i} className="hugi-alerta">{a}</p>)}
              </>
            ) : null}
          </div>
        </aside>
      </div>
    </PaginaDeCadastro>
  );
}

/** O nome de um número do rodízio: o apelido, ou o número pela máscara única (0097). */
function rotuloDoNumero(r: { readonly apelido: string | null; readonly numeroExibicao: string | null }): string {
  return r.apelido ?? (r.numeroExibicao ? identificadorLegivel(r.numeroExibicao) : "número sem nome");
}

/** O que um número do rodízio está fazendo AGORA — o freio primeiro, porque é ele que segura a parte. */
function estadoDoRemetente(r: RemetenteDaPublicacao): { readonly tom: "ok" | "degradado" | "ruim"; readonly rotulo: string; readonly motivo: string | null } {
  if (r.freio.acao === "pausar") return { tom: "ruim", rotulo: "Pausado pelo freio", motivo: r.freio.motivo };
  if (!r.disponivel) return { tom: "degradado", rotulo: "Fora do ar", motivo: r.motivo ? `${r.motivo} — a parte dele espera` : "a parte dele espera" };
  if (r.freio.acao === "desacelerar") return { tom: "degradado", rotulo: "Metade do ritmo", motivo: r.freio.motivo };
  return { tom: "ok", rotulo: "No ar", motivo: null };
}

// --- Miúdos da tela ---------------------------------------------------------------------------------

/** O estado de falha da área, com os estados do kit (e o da casca quando é caso comercial ou de versão). */
function Falha(props: { readonly erro: unknown; readonly aoTentarDeNovo?: () => void }) {
  const f = falhaDe(props.erro);
  if (f?.tipo === "sem_permissao") return <SemPermissao>{f.permissao ? textos.estados.semPermissao.qual(f.permissao) : textos.estados.semPermissao.semNome}</SemPermissao>;
  if (f?.tipo === "sem_entitlement" || f?.tipo === "versao_incompativel") return <EstadoDaFalha falha={f} />;
  return <Erro {...(props.aoTentarDeNovo ? { aoTentarDeNovo: props.aoTentarDeNovo } : {})}>{f?.erro.mensagem ?? textos.estados.erro.corpo}</Erro>;
}

/** Botão do kit; desabilitado, diz o motivo (no `title` e ligado por `aria-describedby`). */
function Acao(props: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly tom?: "primaria" | "perigo";
  readonly pequeno?: boolean;
  readonly desabilitado?: boolean;
  readonly motivo?: string;
  readonly titulo?: string;
}) {
  const id = useId();
  const comMotivo = Boolean(props.desabilitado && props.motivo);
  return (
    <>
      <button
        type="button"
        className={`v2-botao${props.tom === "primaria" ? " is-primario" : props.tom === "perigo" ? " is-perigo" : ""}${props.pequeno ? " is-pequeno" : ""}`}
        disabled={props.desabilitado}
        title={comMotivo ? props.motivo : props.titulo}
        aria-describedby={comMotivo ? id : undefined}
        onClick={props.onClick}
      >
        {props.children}
      </button>
      {comMotivo ? <span id={id} className="v2-sr">{props.motivo}</span> : null}
    </>
  );
}
